/* ═══════════════════════════════════════════════════════════════════
   [D-452] «💶 Фінанси» партнера — підвкладки замість довгого списку
   (вимога OWNER 05.10: «не роби довгі списки вниз, зроби щоб зручно було
   переходити між меню натискаючи кнопки»).

   Оплати · Пакети · Витрати · Розрахунки · Підсумок.
   🔴 Після БУДЬ-ЯКОЇ зміни перезавантажуємо все разом (reloadAll): інакше
   «Додати» оновлювало список, а підсумки й баланс лишались старими.
   ═══════════════════════════════════════════════════════════════════ */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Typography, Button, TextField, MenuItem, Switch, Chip, Dialog, DialogTitle, DialogContent,
  DialogActions, Checkbox, FormControlLabel, Portal,
} from '@mui/material';
import api from '../api';

const C = {
  card: '#16202c', card2: '#1b2836', line: 'rgba(0,229,160,.22)', txt: '#eaf6ff', dim: '#a8bece',
  neon: '#00e5a0', neonTxt: '#7ffcd3', coral: '#ff7a59', warn: '#ffb74d', blue: '#4fc3f7',
};
const CCY = { UAH: '₴', EUR: '€' };
const num = (v) => (v == null ? '—' : (Math.round(Number(v) * 100) / 100).toLocaleString('uk-UA'));
const money = (v, c) => (v == null ? '—' : num(v) + ' ' + (CCY[c] || c || ''));
const thisMonth = () => new Date().toISOString().slice(0, 7);
const MONTHS = ['січень', 'лютий', 'березень', 'квітень', 'травень', 'червень', 'липень', 'серпень', 'вересень', 'жовтень', 'листопад', 'грудень'];
const fmonth = (ym) => { const [y, m] = ym.split('-'); return MONTHS[Number(m) - 1] + ' ' + y; };
// «з жовтня 2026 до листопада 2026» — родовий відмінок після «з/до/у»
const MONTHS_G = ['січня', 'лютого', 'березня', 'квітня', 'травня', 'червня', 'липня', 'серпня', 'вересня', 'жовтня', 'листопада', 'грудня'];
const gmonth = (ym) => { const [y, m] = ym.split('-'); return MONTHS_G[Number(m) - 1] + ' ' + y; };
const fdt = (v) => (v ? new Date(v).toLocaleString('uk-UA', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—');
// [D-471] свій колір кожного місяця (той самий у кабінеті партнера) — місяці розрізняються на око
const MCOL = ['#4fc3f7', '#b39ddb', '#81c784', '#aed581', '#4db6ac', '#ffd54f', '#ffb74d', '#ff8a65', '#f06292', '#ba68c8', '#7986cb', '#90a4ae'];
const mcol = (ym) => MCOL[(Number(String(ym).slice(5, 7)) || 1) - 1];
const fday = (d) => (d ? String(d).slice(8, 10) + '.' + String(d).slice(5, 7) + '.' + String(d).slice(0, 4) : '—');
const today = () => new Date().toISOString().slice(0, 10);
const SET_ST = { pending: ['⏳ чекає', '#ffb74d'], confirmed: ['✅ підтверджено', '#00e5a0'], rejected: ['❌ не підтверджено', '#ff7a59'] };

const fieldSx = { '& .MuiOutlinedInput-notchedOutline': { borderColor: C.line } };
const inProps = { style: { color: C.txt } };
const lblProps = { style: { color: C.dim } };
const dlgPaper = { sx: { bgcolor: '#101a24', backgroundImage: 'none', border: '1px solid ' + C.line, color: C.txt } };
const btnMain = { bgcolor: C.neon, color: '#04121a', textTransform: 'none', fontWeight: 700,
                  '&:hover': { bgcolor: '#00c98c' }, '&.Mui-disabled': { bgcolor: 'rgba(0,229,160,.14)', color: C.dim } };
const btnText = (c) => ({ textTransform: 'none', color: c, fontSize: 12.5, minWidth: 0 });

const SUBS = [
  ['sales', '💳 Оплати'],
  ['packages', '📦 Пакети'],
  ['costs', '🧾 Витрати'],
  ['ai', '🤖 ШІ'],
  ['settle', '🔁 Розрахунки'],
  ['summary', '📊 Підсумок'],
];

const Card = ({ children, color, sx, onClick }) => (
  <Box onClick={onClick} sx={{ bgcolor: C.card2, borderRadius: 2, p: 1.5, mb: 1, borderLeft: `4px solid ${color || C.line}`, position: 'relative',
                               ...(onClick ? { cursor: 'pointer', pr: 3.5, '&:hover': { bgcolor: '#22344a' } } : {}), ...(sx || {}) }}>
    {onClick && <Typography sx={{ position: 'absolute', right: 10, top: 14, color: C.dim, fontSize: 20, lineHeight: 1 }}>›</Typography>}
    {children}
  </Box>
);
const Hint = ({ children }) => <Typography sx={{ fontSize: 12, color: C.dim, mt: .6 }}>{children}</Typography>;
const Stat = ({ label, value, color }) => (
  <Box sx={{ flex: '1 1 125px', minWidth: 0, bgcolor: C.card2, borderRadius: 2, p: 1.1, textAlign: 'center' }}>   {/* [D-623] дві в рядок на телефоні */}
    <Typography sx={{ fontSize: 19, fontWeight: 800, color: color || C.txt }}>{value}</Typography>
    <Typography sx={{ fontSize: 11.5, color: C.dim }}>{label}</Typography>
  </Box>
);

// [D-634] активне віконце: натиск відкриває окреме вікно (› - є подробиці); без onClick - просто плитка
const Tl = ({ l, v, vc, c, sub, onClick, wide }) => (
  <Box onClick={onClick || undefined}
    sx={{ position: 'relative', bgcolor: C.card2, borderRadius: 2, p: 1.2, pr: onClick ? 3.2 : 1.2, borderTop: `3px solid ${c || 'rgba(255,255,255,.08)'}`, minWidth: 0,
          cursor: onClick ? 'pointer' : 'default', gridColumn: wide ? '1 / -1' : 'auto', '&:hover': onClick ? { bgcolor: '#22344a' } : {} }}>
    {onClick && <Typography sx={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', color: C.dim, fontSize: 20, lineHeight: 1 }}>›</Typography>}
    <Typography sx={{ fontSize: 11.5, color: C.dim }}>{l}</Typography>
    <Typography sx={{ fontSize: 16.5, fontWeight: 800, color: vc || C.txt, wordBreak: 'break-word', lineHeight: 1.3 }}>{v}</Typography>
    {sub && <Typography sx={{ fontSize: 11, color: C.dim, lineHeight: 1.35, mt: .2 }}>{sub}</Typography>}
  </Box>
);
const BigV = ({ v, c, sub, top }) => (
  <Box sx={{ bgcolor: C.card2, borderRadius: 2, p: 1.6, mb: 1.2, textAlign: 'center', borderTop: `3px solid ${top || c || C.line}` }}>
    <Typography sx={{ fontSize: 26, fontWeight: 800, color: c || C.txt }}>{v}</Typography>
    {sub && <Typography sx={{ fontSize: 12.5, color: C.dim, lineHeight: 1.5 }}>{sub}</Typography>}
  </Box>
);
// [D-634] чек одразу у вікні (а не «🧾 чек» → нова вкладка): файл тим самим входом власника; картинка - <img>, PDF - вбудовано + «відкрити окремо»
function Receipt({ url, label }) {
  const [f, setF] = useState(null);
  const [err, setErr] = useState('');
  const [bad, setBad] = useState(false);
  const [big, setBig] = useState(false);
  useEffect(() => {
    let u = null, on = true;
    setF(null); setErr(''); setBad(false);
    api.get(url, { responseType: 'blob' })
      .then(r => { if (!on) return; u = URL.createObjectURL(r.data); setF({ u, type: String(r.data.type || '').toLowerCase() }); })
      .catch(() => { if (on) setErr('Файл недоступний'); });
    return () => { on = false; if (u) URL.revokeObjectURL(u); };
  }, [url]);
  const pdf = f && f.type === 'application/pdf';
  const img = f && !pdf && !bad;   // тип з Telegram буває octet-stream - пробуємо як картинку, не вийшло - «відкрити окремо»
  return (
    <Box sx={{ mt: 1.5 }}>
      {label !== '' && <Typography sx={{ fontSize: 12.5, color: C.dim, mb: .6 }}>{label || '🧾 Чек'}</Typography>}
      {err && <Typography sx={{ fontSize: 13, color: C.coral }}>{err}</Typography>}
      {!f && !err && <Typography sx={{ fontSize: 13, color: C.dim }}>Завантаження чека…</Typography>}
      {img && <Box component="img" src={f.u} alt="чек" onError={() => setBad(true)} onClick={() => setBig(true)}
        sx={{ width: '100%', maxHeight: '70vh', objectFit: 'contain', borderRadius: 1.5, bgcolor: '#fff', display: 'block', cursor: 'zoom-in' }} />}
      {pdf && <Box component="iframe" src={f.u} title="чек" sx={{ width: '100%', height: '70vh', border: 0, borderRadius: 1.5, bgcolor: '#fff' }} />}
      {f && <Typography sx={{ fontSize: 11.5, color: C.dim, mt: .4 }}>
        {img ? 'натисніть - на весь екран · ' : !pdf ? 'цей файл браузер не показує у вікні · ' : ''}
        <span onClick={() => window.open(f.u, '_blank')} style={{ color: C.blue, cursor: 'pointer' }}>відкрити окремо</span></Typography>}
      {big && img && (
        <Box onClick={() => setBig(false)} sx={{ position: 'fixed', inset: 0, zIndex: 1350, bgcolor: 'rgba(0,0,0,.94)', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 1.5, cursor: 'zoom-out' }}>
          <Box component="img" src={f.u} alt="чек" sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
        </Box>
      )}
    </Box>
  );
}

export default function PartnerFinance({ p, onChanged, setToast, setErr }) {
  const pid = p.id;
  const [sub, setSub] = useState('sales');
  const [sales, setSales] = useState(null);
  const [sum, setSum] = useState(null);
  const [costs, setCosts] = useState(null);
  const [pkgs, setPkgs] = useState(null);
  const [reg, setReg] = useState([]);
  const [ai, setAi] = useState(null);             // [D-453] ключ (маска) + витрати ШІ
  const [aiKey, setAiKey] = useState('');
  const [sheets, setSheets] = useState([]);       // [D-634] стос окремих вікон: { id, t, title, color, ... }
  const [ver, setVer] = useState(0);              // [D-634] лічильник перезавантажень - чек витрати перечитується після заміни
  const [pkgDlg, setPkgDlg] = useState(null);     // пакет у формі (новий або на зміну)
  const [costDlg, setCostDlg] = useState(null);   // витрата у формі
  const [setDlg, setSetDlg] = useState(null);     // переказ у формі
  const [pfeat, setPfeat] = useState(null);       // [D-471] функції, показані партнеру (для пакета)
  const [pkgArea, setPkgArea] = useState(null);
  const [coopDlg, setCoopDlg] = useState(null);   // [D-620] запуск співпраці
  const [aiAcc, setAiAcc] = useState(null);       // [D-618] рахунок ШІ по місяцях
  const [topDlg, setTopDlg] = useState(null);     // [D-618] поповнення ШІ у формі
  const [aiMore, setAiMore] = useState(false);    // [D-623] ключ GPT і запити - довідка, згорнуто
  const [led, setLed] = useState(null);           // [D-623] зведення місяця (ті самі числа, що бачить партнер)

  const detail = (e) => (e && e.response && e.response.data && e.response.data.detail) || '';
  const fail = (e) => setErr('Не вдалося' + (detail(e) ? ': ' + detail(e) : ''));

  const reloadAll = useCallback(() => {
    api.get(`/partners/${pid}/sales`).then(r => setSales((r.data && r.data.rows) || [])).catch(() => setSales([]));
    api.get(`/partners/${pid}/summary`).then(r => setSum(r.data)).catch(() => {});
    api.get(`/partners/${pid}/costs`).then(r => setCosts((r.data && r.data.rows) || [])).catch(() => setCosts([]));
    api.get(`/partners/${pid}/packages`).then(r => setPkgs((r.data && r.data.rows) || [])).catch(() => setPkgs([]));
    api.get(`/partners/${pid}/ai`).then(r => setAi(r.data)).catch(() => setAi({ months: [], top: [], by_source: [] }));
    api.get(`/partners/${pid}/ai-account`).then(r => setAiAcc(r.data)).catch(() => setAiAcc({ months: [] }));   // [D-618]
    api.get(`/partners/${pid}/ledger`).then(r => setLed(r.data)).catch(() => setLed(null));   // [D-623]
    setVer(v => v + 1);
    onChanged && onChanged();
  }, [pid, onChanged]);
  useEffect(() => { reloadAll(); }, [pid]);   // перший показ
  useEffect(() => {
    api.get('/uniq/feature-registry?area=all').then(r => setReg(Array.isArray(r.data) ? r.data : [])).catch(() => setReg([]));
  }, []);
  // [D-634] вікна: відкрити поверх, закрити верхнє (← Назад / Esc), закрити всі; інший партнер - вікна геть
  const openS = (d) => setSheets(st => [...st, { id: Date.now() + Math.random(), ...d }]);
  const closeS = () => setSheets(st => st.slice(0, -1));
  useEffect(() => { setSheets([]); }, [pid]);
  useEffect(() => {
    if (!sheets.length) return undefined;
    const h = (e) => { if (e.key === 'Escape' && !document.querySelector('.MuiDialog-root')) closeS(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [sheets.length]);
  // [D-471] у пакет можуть входити лише функції, показані партнеру (набір G15 + те, що ви відкрили) — беремо свіжі
  const openPkg = (d) => api.get(`/partners/${pid}/features`)
    .then(r => { setPfeat(((r.data && r.data.rows) || []).filter(f => f.eff_vis)); setPkgArea(null); setPkgDlg(d); })
    .catch(fail);
  const afterChange = (msg) => { setToast(msg); reloadAll(); };

  const pending = (sales || []).filter(s => s.status === 'pending').length;

  // [D-620] «🚀 Запуск співпраці»: з цієї миті все рахується по-справжньому, попереднє - тест
  const coop = sum && sum.coop;
  const saveCoop = () => api.post(`/partners/${pid}/coop`, { start: coopDlg.start ? coopDlg.start : 'now' })
    .then(() => { setCoopDlg(null); afterChange('🚀 Співпрацю запущено'); }).catch(fail);
  const stopCoop = () => {
    if (!window.confirm('Повернути партнера в тестовий режим? (можна, лише поки жоден місяць співпраці не закрито)')) return;
    api.post(`/partners/${pid}/coop`, { start: null }).then(() => afterChange('Повернуто в тестовий режим')).catch(fail);
  };
  // [D-618] рахунок ШІ: поповнення і залишок наприкінці місяця
  const saveTop = () => {
    const d = topDlg;
    const req = d.id ? api.post(`/partners/${pid}/ai-topups/${d.id}`, { ym: d.ym, amount: d.amount, note: d.note })
                     : api.post(`/partners/${pid}/ai-topups`, { ym: d.ym, amount: d.amount, note: d.note });
    req.then(() => { setTopDlg(null); afterChange(d.id ? 'Поповнення виправлено' : 'Поповнення додано'); }).catch(fail);
  };
  // [D-622] чеки рахунку ШІ: поповнення і скрін залишку (їх бачить і партнер)
  const upAi = (q, file) => {
    if (!file) return;
    const fd = new FormData(); fd.append('file', file);
    api.post(`/partners/${pid}/ai-files?${q}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }).then(() => afterChange('Чек збережено')).catch(fail);
  };
  const delAiFile = (f) => { if (window.confirm('Прибрати цей чек?')) api.delete(`/partners/${pid}/ai-files/${f.id}`).then(() => afterChange('Чек прибрано')).catch(fail); };
  const delTop = (t) => { if (window.confirm('Прибрати поповнення ' + money(t.amount, 'EUR') + '?')) api.delete(`/partners/${pid}/ai-topups/${t.id}`).then(() => afterChange('Поповнення прибрано')).catch(fail); };
  const saveEnd = (m, v) => {
    const val = String(v).trim();
    if ((val === '' && m.end == null) || (val !== '' && m.end != null && Number(val.replace(',', '.')) === Number(m.end))) return;
    api.post(`/partners/${pid}/ai-balance`, { ym: m.month, end_amount: val === '' ? null : val }).then(() => afterChange('Залишок ШІ збережено')).catch(fail);
  };

  // ── дії ──────────────────────────────────────────────────────────────────
  const decide = (s, approve) => {
    let reason = '';
    if (!approve) { reason = window.prompt('Причина відхилення (юзер її побачить):', 'Оплату не знайдено'); if (reason === null) return; }
    else if (!window.confirm(`Підтвердити оплату ${money(s.amount, s.ccy)}? ${s.kind === 'v5_tokens' ? 'Учню зарахується ' + s.tokens + ' токенів Студії.' : 'Учню відкриється доступ на місяць.'}`)) return;
    api.post(`/partners/${pid}/sales/${s.id}/decide`, { approve, reason }).then(() => afterChange(approve ? 'Оплату підтверджено' : 'Оплату відхилено')).catch(fail);
  };
  const cancelSale = (s) => {
    if (!window.confirm(`Скасувати підтверджену оплату #${s.id} (${money(s.amount, s.ccy)})?\n${s.kind === 'v5_tokens' ? 'У учня заберуться ' + s.tokens + ' токенів (якщо вже витратив - не скасується)' : 'Доступ учня зменшиться на місяць'}, з підсумків вона зникне.`)) return;
    api.post(`/partners/${pid}/sales/${s.id}/cancel`).then(() => afterChange('Оплату скасовано')).catch(fail);
  };
  // [D-619] виправити записану оплату: сума, що реально надійшла / тестова (не рахується)
  const editSale = (s) => {
    const v = window.prompt(`Оплата #${s.id}: скільки реально надійшло (${s.ccy === 'EUR' ? '€' : '₴'})?\nМісяць перерахується сам; у журналі грошей лишиться запис.`, String(s.amount));
    if (v === null || String(v).trim() === '' || Number(String(v).replace(',', '.')) === Number(s.amount)) return;
    api.post(`/partners/${pid}/sales/${s.id}/edit`, { amount: v }).then(() => afterChange('Суму оплати виправлено')).catch(fail);
  };
  const testSale = (s) => {
    if (!window.confirm(s.is_test ? `Оплата #${s.id} - не тестова: знову рахується в місяці?` : `Позначити оплату #${s.id} тестовою? Вона не рахуватиметься в місяцях і балансі (доступ учня не зміниться).`)) return;
    api.post(`/partners/${pid}/sales/${s.id}/edit`, { is_test: !s.is_test }).then(() => afterChange(s.is_test ? 'Знову рахується' : 'Позначено тестовою')).catch(fail);
  };
  const delSale = (s, done) => {
    if (!window.confirm(`Прибрати оплату #${s.id} зі списку?`)) return;
    api.delete(`/partners/${pid}/sales/${s.id}`).then(() => { afterChange('Прибрано'); done && done(); }).catch(fail);
  };

  const savePkg = () => {
    const d = pkgDlg;
    const body = { name: d.name, price_month: d.price_month, ccy: d.ccy, daily_limit: d.daily_limit,
                   can_publish: !!d.can_publish, closed_keys: d.closed_keys || [] };
    const req = d.id ? api.post(`/partners/${pid}/packages/${d.id}`, body) : api.post(`/partners/${pid}/packages`, body);
    req.then(() => { setPkgDlg(null); afterChange(d.id ? 'Пакет змінено' : 'Пакет створено'); }).catch(fail);
  };
  const delPkg = (k, done) => {
    if (!window.confirm(`Прибрати пакет «${k.name}»? Якщо за нього вже платили — його буде лише сховано з продажу.`)) return;
    api.delete(`/partners/${pid}/packages/${k.id}`).then(r => { afterChange(r.data && r.data.hidden ? 'Пакет сховано з продажу' : 'Пакет видалено'); if (done && !(r.data && r.data.hidden)) done(); }).catch(fail);
  };
  const togglePkg = (k) => api.post(`/partners/${pid}/packages/${k.id}`, { active: !k.active })
    .then(() => afterChange(k.active ? 'Пакет сховано з продажу' : 'Пакет знову в продажу')).catch(fail);

  const saveCost = async () => {
    const d = costDlg;
    const body = { title: d.title, kind: d.kind, amount: d.amount, ccy: d.ccy, start_month: d.start_month,
                   end_month: d.kind === 'monthly' ? (d.end_month || '') : '', paid_by: d.paid_by === 'partner' ? 'partner' : 'owner' };   // [D-558]
    try {
      let cid = d.id;
      if (cid) await api.post(`/partners/${pid}/costs/${cid}`, body);
      else cid = (await api.post(`/partners/${pid}/costs`, body)).data.id;
      if (d.file) {
        const fd = new FormData(); fd.append('file', d.file);
        await api.post(`/partners/${pid}/costs/${cid}/receipt`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      }
      setCostDlg(null); afterChange(d.id ? 'Витрату змінено' : 'Витрату додано');
    } catch (e) { fail(e); }
  };
  const delCost = (c, done) => {
    if (!window.confirm(`Видалити витрату «${c.title}» (${money(c.amount, c.ccy)})?`)) return;
    api.delete(`/partners/${pid}/costs/${c.id}`).then(() => { afterChange('Витрату видалено'); done && done(); }).catch(fail);
  };
  const delCostReceipt = (c) => {
    if (!window.confirm('Видалити чек цієї витрати?')) return;
    api.delete(`/partners/${pid}/costs/${c.id}/receipt`).then(() => afterChange('Чек видалено')).catch(fail);
  };

  const saveSettle = () => {
    const d = setDlg;
    const body = { amount: d.amount, ccy: d.ccy, direction: 'to_partner', paid_on: d.paid_on || null, note: d.note || '' };   // [D-471]
    const req = d.id ? api.post(`/partners/${pid}/settlements/${d.id}`, body) : api.post(`/partners/${pid}/settlements`, body);
    req.then(() => { setSetDlg(null); afterChange(d.id ? 'Переказ змінено' : 'Записано — чекає підтвердження партнера'); }).catch(fail);
  };
  const decideSettle = (x, approve) => {   // [D-471]
    let reason = '';
    if (!approve) { reason = window.prompt('Чому не підтверджуєте? (партнер побачить)', 'Гроші не надійшли'); if (reason === null) return; }
    else if (!window.confirm(`Підтвердити, що ви ОТРИМАЛИ ${money(x.amount, x.ccy)} від партнера (переказ ${fday(x.paid_on)})?`)) return;
    api.post(`/partners/${pid}/settlements/${x.id}/decide`, { approve, reason }).then(() => afterChange(approve ? 'Отримання підтверджено' : 'Позначено: не отримано')).catch(fail);
  };
  const delSettle = (x, done) => {
    if (!window.confirm(x.status === 'confirmed'
      ? `Видалити ПІДТВЕРДЖЕНИЙ переказ ${money(x.amount, x.ccy)}? Баланс зміниться.`
      : `Видалити запис переказу ${money(x.amount, x.ccy)}?`)) return;
    api.delete(`/partners/${pid}/settlements/${x.id}`).then(() => { afterChange('Переказ видалено'); done && done(); }).catch(fail);
  };
  const saveAiKey = () => api.post(`/partners/${pid}/ai/key`, { key: aiKey.trim() })
    .then(r => { setAiKey(''); afterChange(r.data && r.data.verified === false ? 'Ключ збережено, але OpenAI зараз не підтвердив його' : 'Ключ збережено й перевірено'); })
    .catch(fail);
  const delAiKey = () => {
    if (!window.confirm('Прибрати GPT-ключ партнера? Його учні знову йтимуть вашим звичайним ключем, облік ШІ зупиниться.')) return;
    api.delete(`/partners/${pid}/ai/key`).then(() => afterChange('Ключ прибрано')).catch(fail);
  };
  const saveAiSetting = (f, v, old) => {
    if (v === '' || Number(v) === Number(old)) return;
    api.post('/partner-ai/settings', { [f]: Number(v) }).then(() => afterChange('Збережено')).catch(fail);
  };
  const patchPartner = (data, msg) => api.post(`/partners/${pid}`, data).then(() => afterChange(msg || 'Збережено')).catch(fail);

  // ── рядки списків (спільні для вкладок і деталей місяця) ──────────────────
  const SaleRow = ({ s }) => {
    const col = s.status === 'pending' ? C.warn : s.status === 'approved' ? C.neon : s.status === 'cancelled' ? '#8a8f98' : C.coral;
    const st = { pending: '⏳ чекає підтвердження', approved: '✅ підтверджено', rejected: '❌ відхилено', cancelled: '↩ скасовано' }[s.status];
    return (
      <Card color={col} onClick={() => oSale(s)}>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <Typography sx={{ fontWeight: 700, color: C.txt, fontSize: 14 }}>#{s.id} · {s.telegram_username ? '@' + s.telegram_username : 'ID ' + s.tg_id}</Typography>
          {s.package_name && <Chip size="small" label={'📦 ' + s.package_name} sx={{ height: 20, fontSize: 11, color: C.neonTxt, bgcolor: 'rgba(0,229,160,.08)' }} />}
          {s.is_test && <Chip size="small" label="🧪 тест - не рахується" sx={{ height: 20, fontSize: 11, color: C.warn, bgcolor: 'rgba(255,183,77,.12)' }} />}   {/* [D-614] */}
          <Box sx={{ flex: 1 }} />
          <Typography sx={{ fontWeight: 800, color: col }}>{money(s.amount, s.ccy)}</Typography>
        </Box>
        <Typography sx={{ fontSize: 12, color: C.dim, mt: .3 }}>
          {st} · {fdt(s.decided_at || s.created_at)}{s.decided_by ? ' · ' + (s.decided_by === 'owner' ? 'ви' : 'партнер') : ''}
          {s.reject_reason ? ' · ' + s.reject_reason : ''}{s.comment ? ' · 💬 ' + s.comment : ''}
        </Typography>
        {s.status === 'approved' && s.owner_total == null && (   /* [D-614] поділ - раз на місяць */
          <Typography sx={{ fontSize: 12.5, color: C.dim, mt: .4 }}>📅 поділ - у підсумку місяця (оплати місяця мінус витрати місяця)</Typography>
        )}
        {s.status === 'approved' && s.owner_total != null && !s.is_test && (   /* [D-623] у тестової старий поділ не показуємо */
          <Typography sx={{ fontSize: 12.5, color: C.txt, mt: .4 }}>
            вам <b style={{ color: C.neon }}>{money(s.owner_total, s.ccy)}</b>
            <span style={{ color: C.dim }}> (витрати {money(s.cost_cover, s.ccy)}{s.ref_cost ? ', реф ' + money(s.ref_cost, s.ccy) : ''}, частка {money(s.owner_share, s.ccy)})</span>
            {' '}· партнеру <b style={{ color: C.warn }}>{money(s.partner_share, s.ccy)}</b>
            {s.partner_cover > 0 && <span style={{ color: C.dim }}> + його витрати {money(s.partner_cover, s.ccy)}</span>}   {/* [D-558] */}
          </Typography>
        )}
        <Box onClick={stop} sx={{ display: 'flex', gap: .5, mt: .6, flexWrap: 'wrap' }}>
          {s.receipt_kind && <Button size="small" sx={btnText(C.blue)} onClick={() => oSale(s)}>🧾 квитанція</Button>}
          {saleActions(s)}
        </Box>
      </Card>
    );
  };

  const CostRow = ({ c }) => (
    <Card color={c.kind === 'once' ? C.warn : C.blue} onClick={() => openS({ t: 'cost', id: c.id, title: '🧾 ' + c.title, color: C.coral })}>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
        <Chip size="small" label={c.kind === 'once' ? '🟠 разова · вкладено' : '🔵 щомісячна'}
          sx={{ height: 20, fontSize: 11, bgcolor: 'transparent', color: c.kind === 'once' ? C.warn : C.blue,
                border: `1px solid ${c.kind === 'once' ? C.warn : C.blue}` }} />
        <Chip size="small" data-cost-by={c.paid_by || 'owner'} label={c.paid_by === 'partner' ? '🤝 заплатив партнер' : '👤 заплатили ви'}
          sx={{ height: 20, fontSize: 11, bgcolor: c.paid_by === 'partner' ? 'rgba(255,183,77,.14)' : 'rgba(0,229,160,.12)',
                color: c.paid_by === 'partner' ? C.warn : C.neonTxt, border: 0 }} />   {/* [D-558] */}
        <Typography sx={{ fontWeight: 700, color: C.txt, fontSize: 14, flex: 1, minWidth: 140 }}>{c.title}</Typography>
        <Typography sx={{ fontWeight: 800, color: C.txt }}>{money(c.amount, c.ccy)}{c.kind === 'monthly' ? ' / міс' : ''}</Typography>
      </Box>
      <Typography sx={{ fontSize: 12, color: C.dim, mt: .3 }}>
        {c.kind === 'once' ? 'вкладено: ' + fmonth(c.start_month) : 'з ' + gmonth(c.start_month) + (c.end_month ? ' до ' + gmonth(c.end_month) : ' — досі')}
      </Typography>
      <Box onClick={stop} sx={{ display: 'flex', gap: .5, mt: .6, flexWrap: 'wrap' }}>
        {c.has_receipt
          ? <>
              <Button size="small" sx={btnText(C.blue)} onClick={() => openS({ t: 'cost', id: c.id, title: '🧾 ' + c.title, color: C.coral })}>🧾 чек</Button>
              <Button size="small" sx={btnText(C.dim)} onClick={() => delCostReceipt(c)}>прибрати чек</Button>
            </>
          : <Typography sx={{ fontSize: 12, color: '#6e8195', alignSelf: 'center', mr: 1 }}>чека немає</Typography>}
        <Button size="small" sx={btnText(C.neonTxt)} onClick={() => setCostDlg({ ...c, file: null })}>✎ змінити{c.has_receipt ? '' : ' / додати чек'}</Button>
        <Button size="small" sx={btnText(C.coral)} onClick={() => delCost(c)}>🗑 видалити</Button>
      </Box>
    </Card>
  );

  // [D-471] переказ — картка зі статусом; дії залежать від того, хто має підтвердити
  const SettleRow = ({ x }) => {
    const [st, col] = SET_ST[x.status] || SET_ST.confirmed;
    const fromP = x.direction === 'to_owner';
    return (
      <Box onClick={() => oSettle(x)} sx={{ bgcolor: C.card2, borderRadius: 2, p: 1.4, borderTop: `3px solid ${col}`, cursor: 'pointer', '&:hover': { bgcolor: '#22344a' } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography sx={{ fontSize: 12.5, color: C.dim, flex: 1 }}>{fromP ? '➡️ партнер → вам' : '⬅️ ви → партнеру'} ›</Typography>
          {x.is_test && <Chip size="small" label="🧪 тест" sx={{ height: 20, fontSize: 11, color: C.warn }} />}   {/* [D-614] */}
          <Chip size="small" label={st} sx={{ height: 20, fontSize: 11, fontWeight: 700, bgcolor: col, color: '#06120a' }} />
        </Box>
        <Typography sx={{ fontSize: 20, fontWeight: 800, color: C.txt, mt: .4 }}>{money(x.amount, x.ccy)}</Typography>
        <Typography sx={{ fontSize: 12, color: C.dim }}>
          переказ {fday(x.paid_on)}{x.note ? ' · 💬 ' + x.note : ''}
          {x.status === 'pending' ? (fromP ? ' · чекає ВАШОГО підтвердження' : ' · чекає підтвердження партнера') : ''}
          {x.status !== 'pending' && x.decided_at ? ' · вирішено ' + fdt(x.decided_at) : ''}{x.reason ? ' · ' + x.reason : ''}
        </Typography>
        <Box onClick={stop} sx={{ display: 'flex', gap: .5, mt: .8, flexWrap: 'wrap' }}>{settleActions(x)}</Box>
      </Box>
    );
  };

  // ── вкладки ──────────────────────────────────────────────────────────────
  const Sales = () => (
    <>
      {!sales && <Hint>Завантаження…</Hint>}
      {sales && sales.length === 0 && (
        <Hint>Оплат ще не було. Учень бачить у боті («💳 Оплата») пакети й реквізити партнера і надсилає квитанцію;
          підтвердити може партнер у кабінеті або ви тут.</Hint>
      )}
      {(sales || []).map(s => <SaleRow key={s.id} s={s} />)}
    </>
  );

  // групуємо за розділом (а не «підряд»): довідник відсортований за sort_order, і без цього
  // «Посилення» чи «Головне меню бота» трапились би двічі
  const keysByArea = () => {
    const g = [], idx = {};
    (pfeat || []).forEach(f => {   // [D-471] лише показані партнеру
      const t = f.area_title || f.area;
      if (idx[t] === undefined) { idx[t] = g.length; g.push({ t, rows: [] }); }
      g[idx[t]].rows.push(f);
    });
    return g;
  };
  const titleOf = (k) => (reg.find(f => f.key === k) || {}).title || k;

  const Packages = () => (
    <>
      <Typography sx={{ color: C.neonTxt, fontWeight: 700, fontSize: 13.5, mb: .6 }}>🏦 Реквізити партнера (бачать учні при оплаті будь-якого пакета)</Typography>
      <TextField size="small" fullWidth multiline minRows={2} key={'pd' + pid + (p.pay_details || '')} data-guide="pay-details"
        placeholder="Напр.: Монобанк, картка 5375 …, отримувач Іван І."
        defaultValue={p.pay_details || ''}
        onBlur={e => { if ((e.target.value || '') !== (p.pay_details || '')) patchPartner({ pay_details: e.target.value }, 'Реквізити збережено'); }}
        inputProps={inProps} sx={fieldSx} />
      {!(p.pay_details || '').trim() && <Typography sx={{ color: C.coral, fontSize: 12, mt: .5 }}>⚠️ Без реквізитів учні бачать «оплата ще налаштовується».</Typography>}

      {/* [D-645] OWNER: «оплата у партнера буде або в євро - поки підтвердження фото у бот, або у криптовалюті» */}
      <Typography sx={{ color: C.neonTxt, fontWeight: 700, fontSize: 13.5, mt: 2.5, mb: .6 }}>🎟 Оплата токенів Студії учнями (1 € = 100 токенів)</Typography>
      <Typography sx={{ color: C.dim, fontSize: 12, mb: .8, lineHeight: 1.45 }}>Учень бачить ці реквізити, платить і надсилає фото / скрін - партнер підтверджує (як пакет). Поле порожнє - цей спосіб учневі не показується.</Typography>
      {[['tok_pay_eur', '💶 Реквізити в євро (IBAN / Revolut / Wise)', 'Напр.: Revolut @ivan, або IBAN LT12 3456 …, отримувач Іван І.'],
        ['tok_pay_crypto', '🪙 Криптовалюта: мережа й адреса (USDT)', 'Напр.: USDT TRC20 - TXyz…']].map(([k, l, ph]) => (
        <Box key={k} sx={{ mb: 1 }}>
          <Typography sx={{ color: C.txt, fontSize: 12.5, mb: .4 }}>{l}</Typography>
          <TextField size="small" fullWidth multiline minRows={1} key={k + pid + (p[k] || '')} data-guide={k} placeholder={ph}
            defaultValue={p[k] || ''}
            onBlur={e => { if ((e.target.value || '') !== (p[k] || '')) patchPartner({ [k]: e.target.value }, 'Реквізити для токенів збережено'); }}
            inputProps={inProps} sx={fieldSx} />
        </Box>))}
      {!(p.tok_pay_eur || '').trim() && !(p.tok_pay_crypto || '').trim() && <Typography sx={{ color: C.dim, fontSize: 12 }}>Обидва порожні - учні цього партнера не можуть купити токени (бачать «ще не налаштовано»).</Typography>}

      <Box sx={{ display: 'flex', alignItems: 'center', mt: 2.5, mb: 1 }}>
        <Typography sx={{ color: C.neonTxt, fontWeight: 700, fontSize: 13.5, flex: 1 }}>📦 Пакети для учнів</Typography>
        <Button variant="contained" size="small" sx={btnMain} data-guide="pkg-add"
          onClick={() => openPkg({ name: '', price_month: '', ccy: (pkgs && pkgs[0] && pkgs[0].ccy) || 'UAH', daily_limit: 3, can_publish: false, closed_keys: [] })}>
          ➕ Створити пакет
        </Button>
      </Box>
      {!pkgs && <Hint>Завантаження…</Hint>}
      {pkgs && pkgs.length === 0 && <Hint>Пакетів ще немає — учні не мають що купити.</Hint>}
      {(pkgs || []).map(k => (
        <Card key={k.id} color={k.active ? C.neon : '#5b6b7a'} sx={{ opacity: k.active ? 1 : .6 }} onClick={() => openS({ t: 'pkg', id: k.id, title: '📦 ' + k.name, color: C.warn })}>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            <Typography sx={{ fontWeight: 800, color: C.txt, fontSize: 15 }}>📦 {k.name}</Typography>
            {!k.active && <Chip size="small" label="не продається" sx={{ height: 20, fontSize: 11, color: C.dim }} />}
            <Box sx={{ flex: 1 }} />
            <Typography sx={{ fontWeight: 800, color: C.warn, fontSize: 15 }}>{money(k.price_month, k.ccy)} / міс</Typography>
          </Box>
          <Typography sx={{ fontSize: 12.5, color: C.txt, mt: .4 }}>
            🎬 {k.daily_limit} відео на день · {k.can_publish ? '📤 з публікацією' : 'без публікації'} · продано: {k.sold || 0}
          </Typography>
          <Typography sx={{ fontSize: 12, color: C.dim, mt: .3 }}>
            {k.closed_keys.length === 0 ? '🧩 усі функції, відкриті партнеру'
              : '🧩 не входить: ' + k.closed_keys.slice(0, 6).map(titleOf).join(', ') + (k.closed_keys.length > 6 ? ` і ще ${k.closed_keys.length - 6}` : '')}
          </Typography>
          <Box onClick={stop} sx={{ display: 'flex', gap: .5, mt: .6, flexWrap: 'wrap' }}>
            <Button size="small" sx={btnText(C.neonTxt)} onClick={() => openPkg({ ...k })}>✎ змінити</Button>
            <Button size="small" sx={btnText(C.blue)} onClick={() => togglePkg(k)}>{k.active ? '🙈 зняти з продажу' : '👁 повернути в продаж'}</Button>
            <Button size="small" sx={btnText(C.coral)} onClick={() => delPkg(k)}>🗑 видалити</Button>
          </Box>
        </Card>
      ))}
      <Hint>Учень після підтвердженої оплати отримує саме свій пакет: ліміт відео, публікацію й лише функції пакета
        (решту можна ще закрити у «🧩 Функції» партнера або конкретному учню).</Hint>
    </>
  );

  const Costs = () => {
    const list = costs || [];
    const by = {};
    list.forEach(c => { const k = c.ccy + '|' + c.kind; by[k] = (by[k] || 0) + Number(c.amount || 0); });
    const bal = (sum && sum.balance) || {};
    const hasP = list.some(c => c.paid_by === 'partner') || (ai && ai.paid_by === 'partner') || Object.values(bal).some(b => b.pcost_due > 0);   // [D-558]
    return (
      <>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
          {Object.keys(by).length === 0 && <Stat label="витрат ще немає" value="—" />}
          {Object.keys(by).sort().map(k => {
            const [ccy, kind] = k.split('|');
            return <Stat key={k} label={kind === 'once' ? 'вкладено (разові)' : 'щомісяця'} value={money(by[k], ccy)} color={kind === 'once' ? C.warn : C.blue} />;
          })}
          {ai && ai.months.length > 0 && ai.months[0].month === thisMonth() &&
            <Stat label="🤖 ШІ цього місяця (авто)" value={money(ai.months[0].amount, ai.months[0].ccy)} color={C.blue} />}
          {Object.entries(bal).filter(([, b]) => b.cost_due > 0 || b.cost_covered > 0).map(([c, b]) => <Stat key={'cov' + c} label={hasP ? '👤 ваші: покрито' : 'покрито з оплат'} value={money(b.cost_covered, c)} color={C.neon} />)}
          {Object.entries(bal).filter(([, b]) => b.cost_due > 0 || b.cost_covered > 0).map(([c, b]) => <Stat key={'left' + c} label={hasP ? '👤 ваші: ще не покрито' : 'ще не покрито'} value={money(b.cost_left, c)} color={b.cost_left > 0 ? C.coral : C.neon} />)}
          {hasP && Object.entries(bal).filter(([, b]) => b.pcost_due > 0 || b.pcost_covered > 0).map(([c, b]) => <Stat key={'pcov' + c} label="🤝 партнера: покрито" value={money(b.pcost_covered, c)} color={C.warn} />)}
          {hasP && Object.entries(bal).filter(([, b]) => b.pcost_due > 0 || b.pcost_covered > 0).map(([c, b]) => <Stat key={'pleft' + c} label="🤝 партнера: ще не покрито" value={money(b.pcost_left, c)} color={b.pcost_left > 0 ? C.coral : C.warn} />)}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Typography sx={{ color: C.neonTxt, fontWeight: 700, fontSize: 13.5, flex: 1 }}>🧾 Витрати на бота (бачите ви й партнер)</Typography>
          <Button variant="contained" size="small" sx={btnMain}
            onClick={() => setCostDlg({ title: '', kind: 'monthly', amount: '', ccy: 'EUR', start_month: thisMonth(), end_month: '', paid_by: 'owner', file: null })}>
            ➕ Додати витрату
          </Button>
        </Box>
        {!costs && <Hint>Завантаження…</Hint>}
        {costs && costs.length === 0 && <Hint>Витрат ще немає.</Hint>}
        {list.map(c => <CostRow key={c.id} c={c} />)}
        <Hint>Раз на місяць (від 1-го до останнього числа, за Києвом): усі оплати учнів місяця спершу повертають витрати того ж місяця
          тому, хто їх заплатив (ваші — вам, партнера — йому), разом з мінусом, що лишився з минулого місяця. Не вистачило — мінус переходить
          на наступний місяць. Решта ділиться за відсотком. Місяць закривається після 1-го числа; зміна відсотка чи платника діє з поточного місяця,
          закриті лишаються як були.</Hint>
      </>
    );
  };

  const Settle = () => {   // [D-471]
    if (!sum) return <Hint>Завантаження…</Hint>;
    const ownerPct = p.owner_pct == null ? 50 : p.owner_pct;
    const bal = (sum && sum.balance) || {};
    const list = (sum && sum.settlements) || [];
    const mine = list.filter(x => x.status === 'pending' && x.direction === 'to_owner');
    return (
      <>
        <Typography sx={{ color: C.neonTxt, fontWeight: 700, fontSize: 13.5, mb: .8 }}>Розподіл прибутку</Typography>
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap', mb: 2 }}>
          <TextField size="small" type="number" label="Ваша частка, %" key={'op' + pid + ownerPct} defaultValue={ownerPct}
            onBlur={e => { if (Number(e.target.value) !== Number(ownerPct)) patchPartner({ owner_pct: e.target.value }); }}
            InputLabelProps={lblProps} inputProps={{ min: 0, max: 100, step: 1, style: { color: C.txt, fontWeight: 700 } }} sx={{ width: 150, ...fieldSx }} />
          <Typography sx={{ color: C.txt }}>вам <b style={{ color: C.neon }}>{ownerPct}%</b> · партнеру <b style={{ color: C.warn }}>{num(100 - ownerPct)}%</b></Typography>
        </Box>
        {oweTiles()}   {/* [D-634] кожна плитка - окреме вікно */}
        {mine.length > 0 && (<>
          <Typography sx={{ color: C.warn, fontWeight: 800, fontSize: 14, mb: .8 }}>⏳ Партнер повідомив про переказ — підтвердіть отримання</Typography>
          <Box sx={{ display: 'grid', gap: 1, gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', mb: 2 }}>
            {mine.map(x => <SettleRow key={x.id} x={x} />)}
          </Box>
        </>)}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1, flexWrap: 'wrap' }}>
          <Typography sx={{ color: C.neonTxt, fontWeight: 700, fontSize: 13.5, flex: 1 }}>🔁 Усі перекази</Typography>
          <Button size="small" variant="outlined" sx={{ textTransform: 'none', borderColor: C.line, color: C.neonTxt }}
            onClick={() => setSetDlg({ amount: '', ccy: (Object.keys(bal)[0]) || 'EUR', paid_on: today(), note: '' })}>
            ⬅️ Я переказав партнеру
          </Button>
        </Box>
        <Hint>Хто платить — той повідомляє, хто отримує — підтверджує. Партнер повідомляє в кабінеті («💸 Я розрахувався»), а ви
          підтверджуєте тут або кнопкою в його гілці. У баланс іде лише підтверджене.</Hint>
        {list.length === 0 && <Hint>Переказів ще не було.</Hint>}
        <Box sx={{ display: 'grid', gap: 1, gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', mt: 1 }}>
          {list.filter(x => !(x.status === 'pending' && x.direction === 'to_owner')).map(x => <SettleRow key={x.id} x={x} />)}
        </Box>
      </>
    );
  };

const MST = { closed: ['🔒 місяць закрито', '#00e5a0'], open: ['⏳ місяць триває - попередньо', '#ffb74d'],
                test: ['🧪 тестовий місяць (співпрацю не запущено)', '#9fb4c6'] };   // [D-614] [D-620]
  const Summary = () => monthCards();   // [D-634] місяць - окремим вікном (було: деталі розгортались під картками)

  const SRC = { analyze: 'опис відео', variants: 'варіанти підписів', dubbing: 'переклад озвучки' };
  const Ai = () => {
    if (!ai) return <Hint>Завантаження…</Hint>;
    const usd = (v) => '$' + (Math.round(Number(v || 0) * 10000) / 10000).toLocaleString('uk-UA');
    return (
      <>
        <Typography sx={{ color: C.neonTxt, fontWeight: 700, fontSize: 13.5, mt: .5, mb: .8 }}>🧾 Рахунок ШІ (поповнення з картки) - це і є витрата ШІ в грошах</Typography>   {/* [D-618] */}
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mb: 1 }}>
          <Button variant="contained" size="small" sx={btnMain} onClick={() => setTopDlg({ ym: thisMonth(), amount: '', note: '' })}>➕ Поповнення</Button>
          <Typography sx={{ fontSize: 12, color: C.dim, flex: '1 1 300px' }}>ШІ за місяць = залишок на 1-ше + поповнення − залишок наприкінці. Після 1-го числа бот спитає
            залишок у гілці партнера кнопкою «✍️ Ввести суму» і попросить скрін; можна ввести й тут. До кожного поповнення - чек. Чеки бачить і партнер. Усе можна виправити.</Typography>
        </Box>
        {(costs || []).some(c => /gpt|ші|openai/i.test(c.title || '')) && ((aiAcc && aiAcc.months) || []).length > 0 &&
          <Card color={C.coral}><Typography sx={{ fontSize: 12.5, color: C.coral }}>⚠️ У «Витратах» є витрата на GPT/ШІ - разом з рахунком ШІ вона порахується двічі. Приберіть її або рахунок.</Typography></Card>}
        {aiAcc && aiAcc.months.length === 0 && <Hint>Поповнень ще не було.</Hint>}
        <Box sx={{ display: 'grid', gap: 1, gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', mb: 1 }}>
          {((aiAcc && aiAcc.months) || []).map(m => (
            <Card key={m.month} color={m.error ? C.coral : m.need_end ? C.warn : m.cost != null ? C.neon : C.blue} sx={{ mb: 0 }}>
              <Typography onClick={() => oAi(m.month)} sx={{ fontWeight: 800, color: C.txt, cursor: 'pointer' }}>{capM(m.month)} <span style={{ color: C.blue, fontWeight: 500, fontSize: 12.5 }}>відкрити з чеками ›</span></Typography>
              <Typography sx={{ fontSize: 12.5, color: C.dim }}>на 1-ше: <b style={{ color: C.txt }}>{m.start == null ? '—' : money(m.start, 'EUR')}</b> · поповнено: <b style={{ color: C.txt }}>{money(m.topped, 'EUR')}</b></Typography>
              <Box sx={{ display: 'grid', gap: .6, my: .8 }}>   {/* [D-618] кожне поповнення - з явними «змінити / прибрати» */}
                {m.topups.map(t => (
                  <Box key={t.id} sx={{ display: 'flex', gap: .8, alignItems: 'center', flexWrap: 'wrap', bgcolor: 'rgba(79,195,247,.10)', borderRadius: 1.5, px: 1, py: .5 }}>
                    <Typography sx={{ fontSize: 13.5, fontWeight: 800, color: C.txt }}>➕ {money(t.amount, 'EUR')}</Typography>
                    <Typography sx={{ fontSize: 11.5, color: C.dim, flex: '1 1 90px' }}>{fdt(t.created_at)}{t.note ? ' · ' + t.note : ''}</Typography>
                    <Button size="small" sx={btnText(C.neonTxt)} onClick={() => setTopDlg({ id: t.id, ym: m.month, amount: t.amount, note: t.note || '' })}>✏️ Змінити</Button>
                    <Button size="small" sx={btnText(C.coral)} onClick={() => delTop(t)}>🗑 Прибрати</Button>
                    {t.file
                      ? <><Button size="small" sx={btnText(C.blue)} onClick={() => oAi(m.month)}>🧾 чек</Button>
                          <Button size="small" sx={btnText(C.dim)} onClick={() => delAiFile(t.file)}>✕</Button></>
                      : <Button size="small" component="label" sx={btnText(C.blue)}>📎 додати чек
                          <input type="file" hidden accept="image/*,application/pdf" onChange={e => upAi('kind=topup&topup_id=' + t.id, e.target.files && e.target.files[0])} /></Button>}   {/* [D-622] */}
                  </Box>
                ))}
              </Box>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                <TextField size="small" label="залишок наприкінці, €" key={'end' + m.month + m.end} defaultValue={m.end == null ? '' : m.end}
                  onBlur={e => saveEnd(m, e.target.value)} InputLabelProps={lblProps} inputProps={inProps} sx={{ width: 170, ...fieldSx }} />
                <Typography sx={{ fontSize: 13, color: m.cost != null ? C.neon : (m.need_end ? C.warn : C.dim), fontWeight: 700 }}>
                  {m.cost != null ? 'ШІ за місяць: ' + money(m.cost, 'EUR') : m.error ? '⚠️ ' + m.error : m.need_end ? '⏳ чекає залишок' : 'місяць триває'}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: .6, alignItems: 'center', flexWrap: 'wrap', mt: .6 }}>   {/* [D-622] скрін залишку */}
                {m.end_file
                  ? <><Button size="small" sx={btnText(C.blue)} onClick={() => oAi(m.month)}>🧾 скрін залишку{m.end_file.via_bot ? ' (з бота)' : ''}</Button>
                      <Button size="small" sx={btnText(C.dim)} onClick={() => delAiFile(m.end_file)}>✕</Button></>
                  : <Button size="small" component="label" sx={btnText(C.blue)}>📎 скрін залишку
                      <input type="file" hidden accept="image/*,application/pdf" onChange={e => upAi('kind=balance&ym=' + m.month, e.target.files && e.target.files[0])} /></Button>}
              </Box>
            </Card>
          ))}
        </Box>

        <Box data-ai-payer={ai.paid_by || 'owner'} sx={{ display: 'flex', gap: .8, alignItems: 'center', flexWrap: 'wrap', mt: 1 }}>   {/* [D-558] */}
          <Typography sx={{ fontSize: 12.5, color: C.txt }}>Хто платить за ШІ:</Typography>
          {[['owner', '👤 я'], ['partner', '🤝 партнер']].map(([k, l]) => (
            <Button key={k} size="small" data-ai-payer-btn={k} variant={(ai.paid_by || 'owner') === k ? 'contained' : 'outlined'}
              onClick={() => (ai.paid_by || 'owner') !== k && api.post(`/partners/${pid}/ai/payer`, { by: k }).then(() => afterChange('Платника ШІ змінено')).catch(fail)}
              sx={{ textTransform: 'none', borderRadius: 5, fontSize: 12.5, px: 1.5, borderColor: C.line, color: (ai.paid_by || 'owner') === k ? '#04121a' : C.neonTxt,
                    bgcolor: (ai.paid_by || 'owner') === k ? C.neon : 'transparent', '&:hover': { bgcolor: (ai.paid_by || 'owner') === k ? C.neon : 'rgba(0,229,160,.08)' } }}>{l}</Button>
          ))}
        </Box>
        <Hint>Витрата ШІ з рахунку стає витратою місяця: оплати учнів спершу повертають її тому, хто платить за ШІ
          ({(ai.paid_by || 'owner') === 'partner' ? 'зараз — партнеру' : 'зараз — вам'}), і лише решта ділиться за відсотком.</Hint>

        <Button size="small" onClick={() => setAiMore(v => !v)} sx={{ ...btnText(C.dim), mt: 1.5 }}>   {/* [D-623] рідко потрібне - згорнуто */}
          {aiMore ? '▴ сховати' : '▾ GPT-ключ партнера і скільки запитів робили учні (довідка)'}
        </Button>
        {aiMore && <>
        <Card color={ai.has_key ? C.neon : C.warn}>
          <Typography sx={{ color: C.neonTxt, fontWeight: 700, fontSize: 13.5 }}>🔑 GPT-ключ цього партнера</Typography>
          <Typography sx={{ fontSize: 12.5, color: C.txt, mt: .4 }}>
            {ai.has_key
              ? <>Підключено: <b>{ai.key_mask}</b> · з {fdt(ai.key_updated_at)}</>
              : 'Ключа немає — учні партнера йдуть вашим звичайним ключем, і їхні витрати ШІ не видно окремо.'}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField size="small" type="password" autoComplete="off" placeholder={ai.has_key ? 'новий ключ замінить старий' : 'sk-…'}
              value={aiKey} onChange={e => setAiKey(e.target.value)} inputProps={inProps} sx={{ flex: '1 1 260px', ...fieldSx }} />
            <Button variant="contained" size="small" sx={btnMain} disabled={aiKey.trim().length < 20} onClick={saveAiKey}>💾 Зберегти ключ</Button>
            {ai.has_key && <Button size="small" sx={btnText(C.coral)} onClick={delAiKey}>🗑 прибрати</Button>}
          </Box>
          <Hint>Ланцюжок той самий: спершу безкоштовний Gemini; коли черга доходить до GPT — запит іде ключем партнера.
            Ключ ніде не показується повністю; партнер його не бачить.</Hint>
        </Card>
        <Typography sx={{ color: C.neonTxt, fontWeight: 700, fontSize: 13.5, mt: 2, mb: .8 }}>📊 Скільки запитів GPT робили учні - лише довідка (гроші - лише рахунок вище)</Typography>
        {ai.months.length === 0 && <Hint>Витрат ще немає{ai.has_key ? ' — зʼявляться після першого GPT-запиту учня.' : '.'}</Hint>}
        {ai.months.map(m => (
          <Card key={m.month} color={C.blue}>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
              <Typography sx={{ fontWeight: 700, color: C.txt, width: 150 }}>{fmonth(m.month)}</Typography>
              <Typography sx={{ fontSize: 12.5, color: C.dim, flex: 1 }}>
                {m.calls} запитів · {m.tok_in.toLocaleString('uk-UA')} + {m.tok_out.toLocaleString('uk-UA')} токенів
              </Typography>
            </Box>
          </Card>
        ))}
        {(ai.top || []).length > 0 && <>
          <Typography sx={{ color: C.neonTxt, fontWeight: 700, fontSize: 13.5, mt: 2, mb: .8 }}>👥 Хто витратив найбільше цього місяця</Typography>
          {ai.top.map(t => (
            <Typography key={t.tg_id} sx={{ fontSize: 12.5, color: C.txt, py: .3 }}>
              {t.username ? '@' + t.username : 'ID ' + t.tg_id} — <b>{t.calls} запитів</b>
            </Typography>
          ))}
          <Hint>{(ai.by_source || []).map(x => (SRC[x.source] || x.source) + ': ' + x.calls).join(' · ')}</Hint>
        </>}

        </>}
      </>
    );
  };

  // [D-623] OWNER 17.10: «треба робити таким чином щоб це легко сприймалося». Згори - один блок: режим (тест / співпраця з …),
  // поточний місяць - оплати, витрати (хто платить), скільки вам і партнеру, і скільки партнер винен вам. Числа - ті самі, що бачить партнер (/ledger).
  const Head = () => {
    if (!sum) return null;
    const started = coop && coop.started_at;
    const cm = led && led.months && led.months.find(m => m.month === led.now);
    const mon = (cm && cm.money) || {};
    const costSum = (f) => { const t = {}; ((cm && cm.costs) || []).filter(f).forEach(x => { t[x.ccy] = (t[x.ccy] || 0) + Number(x.amount || 0); });
      return Object.entries(t).map(([c, v]) => money(v, c)).join(' + '); };
    const by = (k) => Object.entries(mon).map(([c, x]) => money(x[k], c)).join(' + ');
    const nSales = ((cm && cm.income) || []).filter(x => !x.is_test && !x.pre).length;
    const owes = Object.entries((sum && sum.balance) || {}).map(([c, b]) => money(b.partner_owes_owner, c)).join(' + ');
    const carry = Object.entries(mon).filter(([, x]) => (x.carry_out_owner || 0) + (x.carry_out_partner || 0) > 0)
      .map(([c, x]) => money((x.carry_out_owner || 0) + (x.carry_out_partner || 0), c)).join(' + ');
    const Mini = ({ l, v, c, sub, onClick }) => (   // [D-634] активна: натиск - окреме вікно
      <Box onClick={onClick} sx={{ bgcolor: 'rgba(255,255,255,.04)', borderRadius: 1.5, p: 1, pr: 3, minWidth: 0, position: 'relative', cursor: 'pointer', '&:hover': { bgcolor: 'rgba(255,255,255,.08)' } }}>
        <Typography sx={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', color: C.dim, fontSize: 18, lineHeight: 1 }}>›</Typography>
        <Typography sx={{ fontSize: 17, fontWeight: 800, color: c || C.txt, lineHeight: 1.2 }}>{v || '—'}</Typography>
        <Typography sx={{ fontSize: 11, color: C.dim }}>{l}</Typography>
        {sub && <Typography sx={{ fontSize: 10.5, color: C.dim, lineHeight: 1.3 }}>{sub}</Typography>}
      </Box>
    );
    return (
      <Card color={started ? C.neon : C.warn} sx={{ mb: 1.5 }}>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <Typography sx={{ fontSize: 13.5, fontWeight: 800, color: started ? C.neon : C.warn }}>{started ? '🚀 Співпраця з ' + fdt(started) : '🧪 Тестовий режим'}</Typography>
          <Typography sx={{ fontSize: 11.5, color: C.dim, flex: '1 1 150px' }}>{started ? 'рахується з цієї миті' : 'рахується «як було б», місяці не закриваються'}</Typography>
          {started
            ? <><Button size="small" sx={btnText(C.dim)} onClick={() => setCoopDlg({ start: '' })}>змінити</Button><Button size="small" sx={btnText(C.dim)} onClick={stopCoop}>↩ у тест</Button></>
            : <Button size="small" variant="contained" sx={btnMain} onClick={() => setCoopDlg({ start: '' })}>🚀 Запуск співпраці</Button>}
        </Box>
        {led && <>
          <Typography sx={{ fontSize: 12, color: C.dim, mt: 1, mb: .6 }}>
            {(t => t.charAt(0).toUpperCase() + t.slice(1))(fmonth(led.now))} · {Object.values(mon).some(x => x.status === 'open') ? 'місяць триває, попередньо' : 'поки без оплат і витрат'}
          </Typography>
          <Box sx={{ display: 'grid', gap: .8, gridTemplateColumns: '1fr 1fr' }}>
            <Mini l={'💳 оплати учнів' + (nSales ? ' · ' + nSales : '')} v={by('revenue')} onClick={() => oSales(led.now)} />
            <Mini l="🧾 витрати" v={costSum(() => true)} c={C.coral} onClick={() => oCosts(led.now)}
              sub={'👤 ваші ' + (costSum(x => x.paid_by !== 'partner') || '0') + ' · 🤝 партнера ' + (costSum(x => x.paid_by === 'partner') || '0')} />
            <Mini l="💰 вам з оплат" v={by('owner_total')} c={C.neon} onClick={() => oCalc(led.now)} />
            <Mini l="🤝 партнеру" v={by('partner_total')} c={C.warn} onClick={() => oCalc(led.now)} />
            <Mini l="🔁 партнер винен вам (закриті місяці)" v={owes || money(0, 'EUR')} c={C.warn} sub={carry ? '⏭ мінус на наступний місяць ' + carry : null}
              onClick={() => openS({ t: 'owe', title: '🔁 Партнер винен вам', color: C.warn })} />
            <Mini l="📈 по місяцях" v="усі місяці" sub="кожен місяць окремо" onClick={oMonths} />
          </Box>
        </>}
      </Card>
    );
  };

  // ═══════════════════════════════════════════════════════════════════
  // [D-634] Окремі вікна - як у кабінеті партнера (D-632). OWNER 17.10: «супер, зроби так само у дашборді у Фінансах партнера».
  // Кожне віконце відкривається окремим вікном поверх попереднього («← Назад» / Esc - на крок), чек видно одразу.
  // Вікно - це опис { t, ... }, а не знімок: тіло малюється з поточного стану, тож після дії (підтвердити, змінити, видалити) усе свіже.
  // ═══════════════════════════════════════════════════════════════════
  const kym = (v) => (v ? new Date(v).toLocaleDateString('sv-SE', { timeZone: 'Europe/Kyiv' }).slice(0, 7) : null);
  const kday = (v) => (v ? new Date(v).toLocaleDateString('sv-SE', { timeZone: 'Europe/Kyiv' }) : null);
  const coopAt = coop && coop.started_at;
  const isPre = (s) => !!(coopAt && s.decided_at && new Date(s.decided_at) < new Date(coopAt));
  const who = (s) => (s.telegram_username ? '@' + s.telegram_username : 'ID ' + s.tg_id);
  const capM = (ym) => { if (!ym) return 'весь період'; const t = fmonth(ym); return t.charAt(0).toUpperCase() + t.slice(1); };
  const ledM = (ym) => (led && led.months && led.months.find(m => m.month === ym)) || null;
  const SALE_ST = { pending: '⏳ чекає підтвердження', approved: '✅ підтверджено', rejected: '❌ відхилено', cancelled: '↩ скасовано' };
  const saleCol = (s) => (s.is_test || isPre(s) ? '#6e8195' : s.status === 'pending' ? C.warn : s.status === 'approved' ? C.neon : s.status === 'cancelled' ? '#8a8f98' : C.coral);
  const sumBy = (list, f) => {
    const t = {};
    (list || []).filter(f || (() => true)).forEach(x => { t[x.ccy] = (t[x.ccy] || 0) + Number(x.amount || 0); });
    return Object.entries(t).map(([c, v]) => money(v, c)).join(' + ');
  };
  const stop = (e) => e.stopPropagation();
  const oSale = (s) => openS({ t: 'sale', id: s.id, title: '💳 #' + s.id + ' · ' + who(s), color: C.neon });
  const oSales = (ym, st, title) => openS({ t: 'sales', ym, st, title: title || '💳 Оплати · ' + capM(ym), color: C.neon });
  const oCosts = (ym) => openS({ t: 'costs', ym, title: '🧾 Витрати · ' + (ym ? capM(ym) : 'усі'), color: C.coral });
  const oCalc = (ym) => openS({ t: 'calc', ym, title: '🧮 Як пораховано · ' + capM(ym), color: C.warn });
  const oMonth = (ym) => openS({ t: 'month', ym, title: '📅 ' + capM(ym), color: mcol(ym) });
  const oMonths = () => openS({ t: 'months', title: '📈 По місяцях', color: C.blue });
  const oSettles = (q, title) => openS({ t: 'settles', ...q, title, color: C.blue });
  const oSettle = (x) => openS({ t: 'settle', id: x.id, title: '🔁 Переказ ' + money(x.amount, x.ccy), color: C.blue });
  const oAi = (ym) => openS({ t: 'aimonth', ym, title: '🤖 ШІ · ' + capM(ym), color: C.blue });
  const g2 = { display: 'grid', gap: 1, gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))' };
  const g260 = { display: 'grid', gap: 1, gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))' };

  const saleTile = (s) => (
    <Tl key={s.id} c={saleCol(s)} onClick={() => oSale(s)} l={'#' + s.id + ' · ' + who(s)} v={money(s.amount, s.ccy)} vc={s.is_test || isPre(s) ? C.dim : C.txt}
      sub={(SALE_ST[s.status] || s.status) + ' · ' + fday(s.decided_at || s.created_at) + (s.package_name ? ' · ' + s.package_name : '')
        + (s.is_test ? ' · 🧪 тест - не рахується' : isPre(s) ? ' · до запуску - не рахується' : '')} />
  );
  const saleActions = (s, done) => (<>
    {s.status === 'pending' && <>
      <Button size="small" variant="contained" sx={{ ...btnMain, fontSize: 12 }} onClick={() => decide(s, true)}>✅ Підтвердити</Button>
      <Button size="small" sx={btnText(C.coral)} onClick={() => decide(s, false)}>❌ Відхилити</Button>
    </>}
    {s.status === 'approved' && <Button size="small" sx={btnText(C.coral)} onClick={() => cancelSale(s)}>↩ Скасувати оплату</Button>}
    {(s.status === 'approved' || s.status === 'pending') && <>   {/* [D-619] */}
      <Button size="small" sx={btnText(C.neonTxt)} onClick={() => editSale(s)}>✏️ сума</Button>
      <Button size="small" sx={btnText(s.is_test ? C.neonTxt : C.warn)} onClick={() => testSale(s)}>{s.is_test ? '↩ не тест' : '🧪 тестова'}</Button>
    </>}
    {(s.status === 'rejected' || s.status === 'cancelled') && <Button size="small" sx={btnText(C.dim)} onClick={() => delSale(s, done)}>🗑 прибрати</Button>}
  </>);
  const settleActions = (x, done) => {
    const fromP = x.direction === 'to_owner';
    return (<>
      {x.status === 'pending' && fromP && <>
        <Button size="small" variant="contained" sx={{ ...btnMain, fontSize: 12 }} onClick={() => decideSettle(x, true)}>✅ Отримав</Button>
        <Button size="small" sx={btnText(C.coral)} onClick={() => decideSettle(x, false)}>❌ Не отримав</Button>
      </>}
      {x.status === 'pending' && !fromP && <Button size="small" sx={btnText(C.neonTxt)} onClick={() => setSetDlg({ ...x })}>✎ змінити</Button>}
      {(!fromP || x.status !== 'pending') && <Button size="small" sx={btnText(C.coral)} onClick={() => delSettle(x, done)}>🗑 видалити</Button>}
    </>);
  };
  const upCostReceipt = (c, file) => {
    if (!file) return;
    const fd = new FormData(); fd.append('file', file);
    api.post(`/partners/${pid}/costs/${c.id}/receipt`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }).then(() => afterChange('Чек збережено')).catch(fail);
  };

  // ── оплати (місяць / статус / усі) ──
  const sSales = (x) => {
    const rows = (sales || []).filter(s => (!x.ym || kym(s.decided_at || s.created_at) === x.ym) && (!x.st || s.status === x.st));
    const live = rows.filter(s => s.status === 'approved' && !s.is_test && !isPre(s));
    return (<>
      <BigV v={sumBy(live) || money(0, 'EUR')} c={C.neon} sub={live.length + ' оплат рахуються' + (rows.length > live.length ? ' · ще ' + (rows.length - live.length) + ' інших (чекають / тест / до запуску / відхилені)' : '')} />
      {!sales && <Hint>Завантаження…</Hint>}
      {sales && !rows.length && <Hint>Оплат немає.</Hint>}
      <Box sx={g2}>{rows.map(saleTile)}</Box>
      {rows.length > 0 && <Hint>Натисніть оплату - деталі, квитанція й дії.</Hint>}
    </>);
  };
  // ── одна оплата: усе, квитанція одразу, дії ──
  const sSale = (x) => {
    const s = (sales || []).find(r => r.id === x.id);
    if (!s) return <Hint>{sales ? 'Цієї оплати вже немає.' : 'Завантаження…'}</Hint>;
    const skip = s.is_test ? '🧪 тестова - у розрахунки не входить' : isPre(s) ? 'до запуску співпраці - у розрахунки не входить' : '';
    const ym = kym(s.decided_at);
    return (<>
      <BigV v={money(s.amount, s.ccy)} c={skip ? C.dim : saleCol(s)} top={saleCol(s)}
        sub={<>{SALE_ST[s.status] || s.status}{skip ? <><br /><span style={{ color: C.warn }}>{skip}</span></> : null}</>} />
      <Box sx={{ display: 'flex', gap: .5, mb: 1.2, flexWrap: 'wrap' }}>{saleActions(s, closeS)}</Box>
      <Box sx={g2}>
        <Tl l="👤 учень" v={who(s)} />
        <Tl l={s.kind === 'v5_tokens' ? '🎟 за що' : '📦 пакет'} v={s.package_name || '—'} />
        {s.kind === 'v5_tokens'   /* [D-642] оплата токенів Студії - не доступ, а токени на рахунок учня */
          ? <Tl l="🎟 токенів учню" v={s.tokens || '—'} />
          : <><Tl l="📅 місяців" v={s.months || '—'} />
              <Tl l="доступ учня до" v={s.access_until ? fday(s.access_until) : '—'} /></>}
        <Tl l="надіслано" v={fdt(s.created_at)} />
        <Tl l={(s.status === 'rejected' ? 'відхилено' : s.status === 'cancelled' ? 'скасовано' : 'підтверджено') + (s.decided_by ? ' · ' + (s.decided_by === 'owner' ? 'ви' : 'партнер') : '')}
          v={s.decided_at ? fdt(s.decided_at) : '—'} />
        <Tl wide l="💸 поділ" v={s.status !== 'approved' ? '—' : skip ? 'не рахується' : 'раз на місяць - у підсумку: ' + capM(ym)}
          onClick={s.status === 'approved' && !skip && ym ? () => oMonth(ym) : null} />
      </Box>
      {s.comment && <Typography sx={{ fontSize: 13, color: C.txt, mt: 1 }}>💬 {s.comment}</Typography>}
      {s.reject_reason && <Typography sx={{ fontSize: 13, color: C.coral, mt: 1 }}>Причина: {s.reject_reason}</Typography>}
      {s.receipt_kind ? <Receipt url={`/partners/${pid}/sales/${s.id}/receipt`} label="🧾 Квитанція учня" /> : <Hint>Квитанції немає.</Hint>}
    </>);
  };
  // ── витрати (місяць - як порахував калькулятор; без місяця - усі записи) ──
  const sCosts = (x) => {
    const lm = x.ym ? ledM(x.ym) : null;
    const list = x.ym ? ((lm && lm.costs) || []) : (costs || []);
    return (<>
      <Box sx={{ ...g2, mb: 1.2 }}>
        <Tl l="👤 ваші" v={sumBy(list, c => c.paid_by !== 'partner') || '0'} vc={C.neon} />
        <Tl l="🤝 партнера" v={sumBy(list, c => c.paid_by === 'partner') || '0'} vc={C.warn} />
      </Box>
      {!list.length && <Hint>Витрат {x.ym ? 'цього місяця ' : ''}немає.</Hint>}
      <Box sx={g2}>
        {list.map((c, i) => (
          <Tl key={i} c={c.paid_by === 'partner' ? C.warn : C.blue} l={c.title} v={money(c.amount, c.ccy) + (!x.ym && c.kind === 'monthly' ? ' / міс' : '')} vc={C.coral}
            sub={(c.paid_by === 'partner' ? '🤝 партнер' : '👤 ви') + (c.kind === 'ai' ? ' · ШІ' : c.kind === 'once' ? ' · разова' : c.kind === 'ref' ? ' · реферальні' : ' · щомісячна')
              + (c.has_receipt ? ' · 🧾 є чек' : c.id ? ' · без чека' : '')}
            onClick={c.kind === 'ai' ? () => oAi(c.month || x.ym)
              : c.id ? () => openS({ t: 'cost', id: c.id, ym: x.ym, amt: x.ym ? c.amount : null, title: '🧾 ' + c.title, color: C.coral }) : null} />
        ))}
      </Box>
      <Button variant="contained" size="small" sx={{ ...btnMain, mt: 1.5 }}
        onClick={() => setCostDlg({ title: '', kind: 'monthly', amount: '', ccy: 'EUR', start_month: x.ym || thisMonth(), end_month: '', paid_by: 'owner', file: null })}>➕ Додати витрату</Button>
      <Hint>Натисніть витрату - відкриється її чек і дії.</Hint>
    </>);
  };
  // ── одна витрата: чек одразу, змінити / чек / видалити ──
  const sCost = (x) => {
    const c = (costs || []).find(r => r.id === x.id);
    if (!c) return <Hint>{costs ? 'Цієї витрати вже немає.' : 'Завантаження…'}</Hint>;
    const pb = c.paid_by === 'partner';
    return (<>
      <BigV v={money(x.amt != null ? x.amt : c.amount, c.ccy)} c={C.coral} top={pb ? C.warn : C.blue}
        sub={x.amt != null && x.ym ? 'за ' + fmonth(x.ym) : c.kind === 'monthly' ? 'на місяць' : 'разово'} />
      <Box sx={{ display: 'flex', gap: .5, mb: 1.2, flexWrap: 'wrap' }}>
        <Button size="small" sx={btnText(C.neonTxt)} onClick={() => setCostDlg({ ...c, file: null })}>✎ змінити</Button>
        <Button size="small" component="label" sx={btnText(C.blue)}>📎 {c.has_receipt ? 'замінити чек' : 'додати чек'}
          <input type="file" hidden accept="image/*,application/pdf" onChange={e => upCostReceipt(c, e.target.files && e.target.files[0])} /></Button>
        {c.has_receipt && <Button size="small" sx={btnText(C.dim)} onClick={() => delCostReceipt(c)}>прибрати чек</Button>}
        <Button size="small" sx={btnText(C.coral)} onClick={() => delCost(c, closeS)}>🗑 видалити</Button>
      </Box>
      <Box sx={g2}>
        {c.kind === 'monthly' && <Tl l="💶 ціна" v={money(c.amount, c.ccy) + ' / міс'} />}
        <Tl l="📅 коли" v={c.kind === 'once' ? 'разово, ' + fmonth(c.start_month) : 'з ' + gmonth(c.start_month) + (c.end_month ? ' до ' + gmonth(c.end_month) : ' — досі')} />
        <Tl l="хто заплатив" v={pb ? '🤝 партнер' : '👤 ви'} vc={pb ? C.warn : C.txt} />
        <Tl l="↩ повертається з оплат" v={pb ? 'партнеру' : 'вам'} />
      </Box>
      {c.has_receipt ? <Receipt url={`/partners/${pid}/costs/${c.id}/receipt?v=${ver}`} /> : <Hint>🧾 Чека ще немає - «📎 додати чек». Чек бачите ви й партнер.</Hint>}
    </>);
  };
  // ── рахунок ШІ за місяць: поповнення, залишок, їхні чеки ──
  const sAiMonth = (x) => {
    const m = ((aiAcc && aiAcc.months) || []).find(r => r.month === x.ym);
    if (!m) return <Hint>{aiAcc ? 'Даних рахунку ШІ за цей місяць немає.' : 'Завантаження…'}</Hint>;
    return (<>
      <Box sx={{ ...g2, mb: 1.2 }}>
        <Tl l="на 1-ше" v={m.start == null ? '—' : money(m.start, 'EUR')} />
        <Tl l="➕ поповнено" v={money(m.topped, 'EUR')} />
        <Tl l="залишок у кінці" v={m.end != null ? money(m.end, 'EUR') : m.need_end ? '⏳ чекає' : 'місяць триває'} />
        <Tl l="🤖 витрачено" v={m.cost != null ? money(m.cost, 'EUR') : (m.error ? '⚠️ ' + m.error : '—')} vc={C.coral} />
      </Box>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mb: 1.2 }}>
        <Button variant="contained" size="small" sx={btnMain} onClick={() => setTopDlg({ ym: m.month, amount: '', note: '' })}>➕ Поповнення</Button>
        <TextField size="small" label="залишок наприкінці, €" key={'send' + m.month + m.end} defaultValue={m.end == null ? '' : m.end}
          onBlur={e => saveEnd(m, e.target.value)} InputLabelProps={lblProps} inputProps={inProps} sx={{ width: 170, ...fieldSx }} />
      </Box>
      <Box sx={g260}>
        {m.topups.map(t => (
          <Card key={t.id} color={C.blue} sx={{ mb: 0 }}>
            <Typography sx={{ fontSize: 15, fontWeight: 800, color: C.txt }}>➕ {money(t.amount, 'EUR')}</Typography>
            <Typography sx={{ fontSize: 11.5, color: C.dim }}>{fdt(t.created_at)}{t.note ? ' · ' + t.note : ''}</Typography>
            <Box sx={{ display: 'flex', gap: .5, flexWrap: 'wrap', mt: .4 }}>
              <Button size="small" sx={btnText(C.neonTxt)} onClick={() => setTopDlg({ id: t.id, ym: m.month, amount: t.amount, note: t.note || '' })}>✏️ Змінити</Button>
              <Button size="small" sx={btnText(C.coral)} onClick={() => delTop(t)}>🗑 Прибрати</Button>
              {t.file ? <Button size="small" sx={btnText(C.dim)} onClick={() => delAiFile(t.file)}>прибрати чек</Button>
                : <Button size="small" component="label" sx={btnText(C.blue)}>📎 додати чек
                    <input type="file" hidden accept="image/*,application/pdf" onChange={e => upAi('kind=topup&topup_id=' + t.id, e.target.files && e.target.files[0])} /></Button>}
            </Box>
            {t.file && <Receipt url={`/partners/${pid}/ai-files/${t.file.id}`} label="🧾 Чек поповнення" />}
          </Card>
        ))}
        <Card color={C.neon} sx={{ mb: 0 }}>
          <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: C.txt }}>🧾 Скрін залишку наприкінці місяця{m.end_file && m.end_file.via_bot ? ' (з бота)' : ''}</Typography>
          {m.end_file
            ? <><Button size="small" sx={btnText(C.dim)} onClick={() => delAiFile(m.end_file)}>прибрати скрін</Button>
                <Receipt url={`/partners/${pid}/ai-files/${m.end_file.id}`} label="" /></>
            : <Button size="small" component="label" sx={btnText(C.blue)}>📎 додати скрін
                <input type="file" hidden accept="image/*,application/pdf" onChange={e => upAi('kind=balance&ym=' + m.month, e.target.files && e.target.files[0])} /></Button>}
        </Card>
      </Box>
      <Hint>ШІ за місяць = залишок на 1-ше + поповнення − залишок наприкінці. Чеки бачить і партнер.</Hint>
    </>);
  };
  // ── «як пораховано»: кроки калькулятора за місяць (або весь період) ──
  const sCalc = (x) => {
    if (!led) return <Hint>Завантаження…</Hint>;
    const lm = x.ym ? ledM(x.ym) : null;
    const mm = x.ym ? ((lm && lm.money) || {}) : (led.all || {});
    const ent = Object.entries(mm);
    if (!ent.length) return <Hint>Оплат і витрат немає.</Hint>;
    return ent.map(([c, v]) => {
      const n = (k) => Number(v[k] || 0);
      const cin = n('carry_in_owner') + n('carry_in_partner'), cout = n('carry_out_owner') + n('carry_out_partner');
      const opct = v.owner_pct != null ? Number(v.owner_pct) : (p.owner_pct != null ? Number(p.owner_pct) : null);
      const pc = v.partner_cover != null ? v.partner_cover : v.cover_partner;
      const st = MST[v.status];
      return (
        <Box key={c} sx={{ mb: 2 }}>
          {v.status && <Typography sx={{ fontSize: 12.5, color: st ? st[1] : C.coral, mb: 1 }}>{st ? st[0] : '⚠️ ' + (v.why || v.status)}</Typography>}
          <Box sx={g2}>
            <Tl l="💳 оплати учнів" v={money(v.revenue, c)} vc={C.neon} onClick={() => oSales(x.ym, x.ym ? null : 'approved')} />
            <Tl l="➖ ваші витрати" v={money(v.costs_owner, c)} onClick={() => oCosts(x.ym)} />
            <Tl l="➖ витрати партнера" v={money(v.costs_partner, c)} onClick={() => oCosts(x.ym)} />
            <Tl l="➖ мінус минулого місяця" v={money(cin, c)} />
            <Tl wide l="＝ прибуток (що лишилось після витрат)" v={money(v.profit, c)} vc={C.blue} />
            <Tl l={'👤 ваша частка' + (opct != null ? ' ' + opct + '%' : '')} v={money(v.owner_share, c)} vc={C.neon} />
            <Tl l={'🤝 частка партнера' + (opct != null ? ' ' + num(100 - opct) + '%' : '')} v={money(v.partner_share, c)} vc={C.warn} />
            {n('cost_cover') > 0 && <Tl l="↩ ваші витрати назад" v={money(v.cost_cover, c)} />}
            {Number(pc || 0) > 0 && <Tl l="↩ витрати партнера назад" v={money(pc, c)} />}
            <Tl wide l="💰 вам разом (ваші витрати назад + ваша частка)" v={money(v.owner_total, c)} vc={C.neon} />
            <Tl wide l="🤝 партнеру разом" v={money(v.partner_total, c)} vc={C.warn} />
            {cout > 0 && <Tl wide l="⏭ не покрито" vc={C.coral}
              v={money(cout, c) + (v.status === 'open' ? ' - якщо оплат не вистачить, перейде на наступний місяць' : ' - перейшло на наступний місяць')} />}
          </Box>
          {typeof v.explain === 'string' && v.explain && (
            <Card color={C.line} sx={{ mt: 1 }}>
              <Typography sx={{ fontSize: 12, color: C.dim, mb: .3 }}>🧮 Розшифровка калькулятора</Typography>
              <Typography sx={{ fontSize: 12.5, color: C.txt, whiteSpace: 'pre-wrap' }}>{v.explain}</Typography>
            </Card>
          )}
        </Box>
      );
    });
  };
  // ── картки місяців (вкладка «Підсумок» і вікно «По місяцях»): натиск - окреме вікно місяця ──
  const monthCards = () => {
    if (!sum) return <Hint>Завантаження…</Hint>;
    const rows = sum.months.filter(r => r.new_users || r.videos || Object.keys(r.money || {}).length || Object.keys(r.costs || {}).length || Object.keys(r.settle || {}).length);
    const mm = (m, k) => Object.entries(m || {}).map(([c, v]) => money(v[k], c)).join(' + ');
    const Big = ({ l, v, c }) => (
      <Box sx={{ bgcolor: 'rgba(255,255,255,.04)', borderRadius: 1.5, p: 1 }}>
        <Typography sx={{ fontSize: 15.5, fontWeight: 800, color: c || C.txt, lineHeight: 1.2 }}>{v || '—'}</Typography>
        <Typography sx={{ fontSize: 11, color: C.dim }}>{l}</Typography>
      </Box>
    );
    return (<>
      {rows.length === 0 && <Hint>Поки нічого не відбувалось — місяці з’являться тут, щойно будуть юзери, оплати чи витрати.</Hint>}
      <Box sx={{ display: 'grid', gap: 1.2, gridTemplateColumns: 'repeat(auto-fill, minmax(290px, 1fr))' }}>
        {rows.map(r => {
          const n = Object.values(r.money || {}).reduce((a, v) => a + v.sales, 0);
          const mst = Object.entries(r.money || {}), mok = mst.some(([, x]) => x.owner_total != null);
          const col = mcol(r.month);
          const empty = !(n || mst.length || Object.keys(r.costs || {}).length || Object.keys(r.settle || {}).length);
          return (
            <Box key={r.month} onClick={() => oMonth(r.month)}
              sx={{ cursor: 'pointer', position: 'relative', borderRadius: 2.5, p: 1.5, pr: 3.5, bgcolor: C.card2, borderTop: `5px solid ${col}`, '&:hover': { bgcolor: col + '18' } }}>
              <Typography sx={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', color: C.dim, fontSize: 22 }}>›</Typography>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: empty ? 0 : 1 }}>
                <Typography sx={{ fontWeight: 800, fontSize: 17, color: col, flex: 1 }}>{capM(r.month)}</Typography>
                <Typography sx={{ fontSize: 12, color: C.dim }}>👥 +{r.new_users} · 🎬 {r.videos}</Typography>
              </Box>
              {empty ? <Typography sx={{ fontSize: 12, color: C.dim }}>оплат і витрат не було</Typography>
                : <Box sx={{ display: 'grid', gap: .8, gridTemplateColumns: '1fr 1fr' }}>
                    <Big l={'💳 оплати' + (n ? ' (' + n + ')' : '')} v={mst.length ? mm(r.money, 'revenue') : ''} />
                    <Big l="🧾 витрати" v={Object.entries(r.costs || {}).filter(([, v]) => Number(v) > 0).map(([c, v]) => money(v, c)).join(' + ') || '0'} c={C.coral} />
                    <Big l="💰 вам" v={mok ? mm(r.money, 'owner_total') : ''} c={C.neon} />
                    <Big l="🤝 партнеру" v={mok ? mm(r.money, 'partner_total') : ''} c={C.warn} />
                  </Box>}
              {mst.length > 0 && (
                <Box sx={{ display: 'flex', gap: .6, flexWrap: 'wrap', mt: 1 }}>
                  {mst.map(([c, x]) => <Chip key={'st' + c} size="small" label={(MST[x.status] || ['⚠️ ' + (x.why || x.status)])[0]}
                    sx={{ height: 22, fontSize: 11, color: (MST[x.status] || [0, C.coral])[1], bgcolor: 'rgba(255,255,255,.05)' }} />)}
                </Box>
              )}
            </Box>
          );
        })}
      </Box>
      <Hint>Натисніть місяць - його оплати, витрати, перекази і «як пораховано» окремим вікном.</Hint>
    </>);
  };
  // ── один місяць ──
  const sMonth = (x) => {
    const r = ((sum && sum.months) || []).find(m => m.month === x.ym) || { money: {}, costs: {}, settle: {}, new_users: 0, videos: 0 };
    const lm = ledM(x.ym);
    const mm = (lm && Object.keys(lm.money || {}).length ? lm.money : r.money) || {};
    const msales = (sales || []).filter(s => kym(s.decided_at || s.created_at) === x.ym);
    const live = msales.filter(s => s.status === 'approved' && !s.is_test && !isPre(s));
    const msets = ((sum && sum.settlements) || []).filter(s => String(s.paid_on).slice(0, 7) === x.ym);
    const mcosts = (lm && lm.costs) || [];
    const by = (k) => Object.entries(mm).map(([c, v]) => money(v[k], c)).join(' + ') || '—';
    return (<>
      <Box sx={{ display: 'flex', gap: .6, flexWrap: 'wrap', mb: 1.2 }}>
        {Object.entries(mm).map(([c, v]) => <Chip key={c} size="small" label={(MST[v.status] || ['⚠️ ' + (v.why || v.status)])[0]}
          sx={{ height: 22, fontSize: 11, color: (MST[v.status] || [0, C.coral])[1], bgcolor: 'rgba(255,255,255,.05)' }} />)}
      </Box>
      <Box sx={g2}>
        <Tl l={'💳 оплати учнів · ' + live.length} v={sumBy(live) || money(0, 'EUR')} vc={C.neon} c={C.neon}
          sub={msales.length > live.length ? 'записів усього ' + msales.length : 'хто оплатив'} onClick={() => oSales(x.ym)} />
        <Tl l="🧾 витрати" v={sumBy(mcosts) || '0'} vc={C.coral} c={C.coral}
          sub={mcosts.length + ' шт.' + (mcosts.some(c => c.has_receipt) ? ' · 🧾 є чеки' : '')} onClick={() => oCosts(x.ym)} />
        <Tl l="💰 вам" v={by('owner_total')} vc={C.neon} c={C.neon} sub="🧮 як пораховано" onClick={() => oCalc(x.ym)} />
        <Tl l="🤝 партнеру" v={by('partner_total')} vc={C.warn} c={C.warn} sub="🧮 як пораховано" onClick={() => oCalc(x.ym)} />
        <Tl wide l="🔁 перекази цього місяця" v={msets.length ? sumBy(msets) : 'не було'} c={C.blue} sub={msets.length ? msets.length + ' шт.' : null}
          onClick={() => oSettles({ ym: x.ym }, '🔁 Перекази · ' + capM(x.ym))} />
        <Tl l="👥 нових учнів" v={'+' + (r.new_users || 0)} />
        <Tl l="🎬 відео учнів" v={r.videos || 0} />
      </Box>
      <Hint>Підсумок рахується з цих записів: змініть чи видаліть запис — місяць перерахується одразу.</Hint>
    </>);
  };
  // ── перекази (за місяцем / статусом) ──
  const sSettles = (x) => {
    const list = ((sum && sum.settlements) || []).filter(s => (!x.ym || String(s.paid_on).slice(0, 7) === x.ym) && (!x.st || s.status === x.st) && (!x.dir || s.direction === x.dir));
    return (<>
      {!sum && <Hint>Завантаження…</Hint>}
      {sum && !list.length && <Hint>Переказів немає.</Hint>}
      <Box sx={g260}>{list.map(s => <SettleRow key={s.id} x={s} />)}</Box>
      <Button size="small" variant="outlined" sx={{ textTransform: 'none', borderColor: C.line, color: C.neonTxt, mt: 1.5 }}
        onClick={() => setSetDlg({ amount: '', ccy: (Object.keys((sum && sum.balance) || {})[0]) || 'EUR', paid_on: today(), note: '' })}>⬅️ Я переказав партнеру</Button>
    </>);
  };
  // ── один переказ ──
  const sSettle = (x) => {
    const s = ((sum && sum.settlements) || []).find(r => r.id === x.id);
    if (!s) return <Hint>{sum ? 'Цього переказу вже немає.' : 'Завантаження…'}</Hint>;
    const [st, col] = SET_ST[s.status] || SET_ST.confirmed;
    const fromP = s.direction === 'to_owner';
    const skip = s.is_test ? '🧪 тестовий - у баланс не йде' : (coopAt && String(s.paid_on).slice(0, 10) < kday(coopAt) ? 'до запуску співпраці - у баланс не йде' : '');
    return (<>
      <BigV v={money(s.amount, s.ccy)} c={skip ? C.dim : C.txt} top={col}
        sub={<>{fromP ? '➡️ партнер → вам' : '⬅️ ви → партнеру'} · {st}{skip ? <><br /><span style={{ color: C.warn }}>{skip}</span></> : null}</>} />
      <Box sx={{ display: 'flex', gap: .5, mb: 1.2, flexWrap: 'wrap' }}>{settleActions(s, closeS)}</Box>
      <Box sx={g2}>
        <Tl l="📅 дата переказу" v={fday(s.paid_on)} onClick={() => oMonth(String(s.paid_on).slice(0, 7))} />
        <Tl l="записано" v={fdt(s.created_at)} />
        <Tl l={s.status === 'rejected' ? 'не підтверджено' : 'підтверджено'} v={s.decided_at ? fdt(s.decided_at) : '—'} />
        <Tl l="хто підтверджує" v={fromP ? '👤 ви' : '🤝 партнер'} />
        {s.note && <Tl wide l="💬 примітка" v={s.note} />}
        {s.reason && <Tl wide l="причина" v={s.reason} vc={C.coral} />}
      </Box>
    </>);
  };
  // ── «партнер винен вам»: з чого складається ──
  const oweTiles = () => {
    if (!sum) return <Hint>Завантаження…</Hint>;
    const bal = sum.balance || {};
    return (<>
      {Object.keys(bal).length === 0 && <Hint>Підтверджених оплат ще немає.</Hint>}
      {Object.entries(bal).map(([c, b]) => (
        <Box key={c} sx={{ ...g2, mb: 1.5 }}>
          <Tl wide l={b.partner_owes_owner >= 0 ? '🔁 партнер винен вам' : 'переплата партнера (ви винні йому)'} v={money(Math.abs(b.partner_owes_owner), c)}
            vc={b.partner_owes_owner > 0 ? C.warn : b.partner_owes_owner < 0 ? C.blue : C.neon} c={C.warn} sub="= вам належить (закриті місяці) − переказано вам (підтверджено)" />
          <Tl l="вам належить (закриті місяці)" v={money(b.owner_due, c)} vc={C.neon} onClick={oMonths} />
          <Tl l="✅ переказано вам" v={money(b.settled, c)} onClick={() => oSettles({ st: 'confirmed', dir: 'to_owner' }, '✅ Партнер переказав вам')} />
          <Tl l="⏳ чекає вашого підтвердження" v={money(b.pending_to_owner || 0, c)} vc={(b.pending_to_owner || 0) > 0 ? C.warn : C.dim}
            onClick={() => oSettles({ st: 'pending', dir: 'to_owner' }, '⏳ Чекають вашого підтвердження')} />
          <Tl l="⏳ цей місяць, попередньо" v={money(b.open_owner_total || 0, c)} onClick={led ? () => oCalc(led.now) : null} />
          <Tl l="оплат отримано партнером" v={money(b.revenue, c)} onClick={() => oSales(null, 'approved', '💳 Усі підтверджені оплати')} />
        </Box>
      ))}
      <Hint>Тестові перекази і перекази до запуску співпраці в баланс не йдуть.</Hint>
    </>);
  };
  // ── пакет ──
  const sPkg = (x) => {
    const k = (pkgs || []).find(r => r.id === x.id);
    if (!k) return <Hint>{pkgs ? 'Цього пакета вже немає.' : 'Завантаження…'}</Hint>;
    return (<>
      <BigV v={money(k.price_month, k.ccy) + ' / міс'} c={C.warn} top={k.active ? C.neon : '#5b6b7a'} sub={k.active ? 'у продажу' : 'не продається'} />
      <Box sx={{ display: 'flex', gap: .5, mb: 1.2, flexWrap: 'wrap' }}>
        <Button size="small" sx={btnText(C.neonTxt)} onClick={() => openPkg({ ...k })}>✎ змінити</Button>
        <Button size="small" sx={btnText(C.blue)} onClick={() => togglePkg(k)}>{k.active ? '🙈 зняти з продажу' : '👁 повернути в продаж'}</Button>
        <Button size="small" sx={btnText(C.coral)} onClick={() => delPkg(k, closeS)}>🗑 видалити</Button>
      </Box>
      <Box sx={g2}>
        <Tl l="🎬 відео на день" v={k.daily_limit} />
        <Tl l="📤 публікація" v={k.can_publish ? 'так' : 'ні'} />
        <Tl l="🧾 продано" v={k.sold || 0} onClick={() => oSales(null, 'approved', '💳 Підтверджені оплати')} />
        <Tl l="🧩 функції" v={k.closed_keys.length ? 'не входить ' + k.closed_keys.length : 'усі, відкриті партнеру'} />
      </Box>
      {k.closed_keys.length > 0 && <>
        <Typography sx={{ fontSize: 12.5, color: C.dim, mt: 1.5, mb: .6 }}>🧩 Не входить у пакет:</Typography>
        <Box sx={{ display: 'flex', gap: .6, flexWrap: 'wrap' }}>{k.closed_keys.map(f => <Chip key={f} size="small" label={titleOf(f)} sx={{ fontSize: 11.5, color: C.txt, bgcolor: 'rgba(255,255,255,.06)' }} />)}</Box>
      </>}
    </>);
  };
  const SHEET = { sales: sSales, sale: sSale, costs: sCosts, cost: sCost, aimonth: sAiMonth, calc: sCalc, months: monthCards, month: sMonth,
                  settles: sSettles, settle: sSettle, owe: oweTiles, pkg: sPkg };

  const BODY = { sales: Sales, packages: Packages, costs: Costs, ai: Ai, settle: Settle, summary: Summary };

  return (
    <>
      <Head />   {/* [D-623] зведення місяця: що зараз, одним поглядом */}
      <Box sx={{ display: 'flex', gap: .7, flexWrap: 'wrap', mb: 2 }}>
        {SUBS.map(([k, l]) => (
          <Button key={k} size="small" data-guide={'fin-' + k} onClick={() => setSub(k)} variant={sub === k ? 'contained' : 'outlined'}
            sx={{ textTransform: 'none', borderRadius: 5, fontSize: 12.5, px: 1.6, borderColor: C.line,
                  color: sub === k ? '#04121a' : (k === 'sales' && pending ? C.warn : C.neonTxt),
                  bgcolor: sub === k ? C.neon : 'transparent', '&:hover': { borderColor: C.neon, bgcolor: sub === k ? C.neon : 'rgba(0,229,160,.08)' } }}>
            {l}{k === 'sales' && pending ? ` · ${pending}` : ''}{k === 'packages' && pkgs ? ` (${pkgs.filter(x => x.active).length})` : ''}
          </Button>
        ))}
      </Box>
      {BODY[sub]()}

      <Portal>{/* [D-634] у корінь сторінки: усередині картки партнера position:fixed тримався картки, а не екрана */}
      {sheets.map((x, i) => (   /* [D-634] окремі вікна стосом */
        <Box key={x.id} sx={{ position: 'fixed', inset: 0, zIndex: 1200, bgcolor: '#0d1620', overflowY: 'auto', display: i === sheets.length - 1 ? 'block' : 'none' }}>
          <Box sx={{ position: 'sticky', top: 0, zIndex: 2, bgcolor: '#0d1620', borderBottom: `1px solid ${(x.color || C.neon)}55`, px: 2, py: 1 }}>
            <Box sx={{ maxWidth: 960, mx: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
              <Button size="small" onClick={closeS} sx={{ textTransform: 'none', fontWeight: 700, color: C.neon, border: `1px solid ${C.line}`, bgcolor: 'rgba(0,229,160,.10)', flexShrink: 0 }}>← Назад</Button>
              <Typography sx={{ flex: 1, minWidth: 0, fontWeight: 800, fontSize: 16, color: x.color || C.txt, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{x.title}</Typography>
              {sheets.length > 1 && <Button size="small" onClick={() => setSheets([])} sx={btnText(C.dim)}>✕ усі</Button>}
            </Box>
          </Box>
          <Box sx={{ maxWidth: 960, mx: 'auto', p: 2, pb: 8 }}>{(SHEET[x.t] || (() => null))(x)}</Box>
        </Box>
      ))}
      </Portal>

      {/* ── форма пакета ── */}
      <Dialog open={!!pkgDlg} onClose={() => setPkgDlg(null)} maxWidth="md" fullWidth PaperProps={dlgPaper}>
        {pkgDlg && (<>
          <DialogTitle>{pkgDlg.id ? 'Змінити пакет' : 'Новий пакет для учнів'}</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', gap: 1.2, flexWrap: 'wrap', mt: 1 }}>
              <TextField size="small" label="Назва" value={pkgDlg.name} onChange={e => setPkgDlg(d => ({ ...d, name: e.target.value }))}
                InputLabelProps={lblProps} inputProps={inProps} sx={{ width: 220, ...fieldSx }} />
              <TextField size="small" type="number" label="Ціна за місяць" value={pkgDlg.price_month} onChange={e => setPkgDlg(d => ({ ...d, price_month: e.target.value }))}
                InputLabelProps={lblProps} inputProps={{ min: 0, style: { color: C.txt } }} sx={{ width: 140, ...fieldSx }} />
              <TextField size="small" select label="Валюта" value={pkgDlg.ccy} onChange={e => setPkgDlg(d => ({ ...d, ccy: e.target.value }))}
                InputLabelProps={lblProps} SelectProps={{ sx: { color: C.txt } }} sx={{ width: 100, ...fieldSx }}>
                <MenuItem value="UAH">₴</MenuItem><MenuItem value="EUR">€</MenuItem>
              </TextField>
              <TextField size="small" type="number" label="Відео на день" value={pkgDlg.daily_limit} onChange={e => setPkgDlg(d => ({ ...d, daily_limit: e.target.value }))}
                InputLabelProps={lblProps} inputProps={{ min: 1, max: 1000, style: { color: C.txt } }} sx={{ width: 130, ...fieldSx }} />
              <FormControlLabel sx={{ color: C.txt }} control={<Switch checked={!!pkgDlg.can_publish} onChange={e => setPkgDlg(d => ({ ...d, can_publish: e.target.checked }))} />}
                label="з публікацією" />
            </Box>
            <Typography sx={{ color: C.neonTxt, fontWeight: 700, mt: 2, mb: .3 }}>🧩 Функції, що входять у пакет</Typography>
            {(() => {   /* [D-471] лише те, що показано партнеру (набір G15 + відкрите вами); розділи — кнопками */
              const groups = keysByArea();
              const shownKeys = (pfeat || []).map(f => f.key);
              const offN = (pkgDlg.closed_keys || []).filter(k => shownKeys.includes(k)).length;
              const g = groups.find(x => x.t === pkgArea) || groups[0];
              return (<>
                <Hint>Тут лише функції, показані цьому партнеру ({shownKeys.length}). Зніміть галочку — у цьому пакеті функції не буде.
                  Зараз у пакеті: <b style={{ color: C.neon }}>{shownKeys.length - offN}</b> з {shownKeys.length}.</Hint>
                <Box sx={{ display: 'flex', gap: 1, my: 1 }}>
                  <Button size="small" sx={btnText(C.neon)} onClick={() => setPkgDlg(d => ({ ...d, closed_keys: (d.closed_keys || []).filter(k => !shownKeys.includes(k)) }))}>✓ усі</Button>
                  <Button size="small" sx={btnText(C.coral)} onClick={() => setPkgDlg(d => ({ ...d, closed_keys: Array.from(new Set([...(d.closed_keys || []), ...shownKeys])) }))}>✕ жодної</Button>
                </Box>
                {groups.length === 0 && <Hint>Партнеру ще нічого не показано — спершу «🧩 Функції».</Hint>}
                <Box sx={{ display: 'flex', gap: .7, flexWrap: 'wrap', mb: 1.2 }}>
                  {groups.map(x => {
                    const on = g && x.t === g.t, off = x.rows.filter(f => (pkgDlg.closed_keys || []).includes(f.key)).length;
                    return (
                      <Button key={x.t} size="small" onClick={() => setPkgArea(x.t)} variant={on ? 'contained' : 'outlined'}
                        sx={{ textTransform: 'none', borderRadius: 5, fontSize: 12, borderColor: C.line, color: on ? '#04121a' : C.neonTxt, bgcolor: on ? C.neon : 'transparent' }}>
                        {x.t} · {x.rows.length - off}/{x.rows.length}</Button>
                    );
                  })}
                </Box>
                {g && (
                  <Box sx={{ display: 'grid', gap: .8, gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))' }}>
                    {g.rows.map(f => {
                      const on = !(pkgDlg.closed_keys || []).includes(f.key);
                      return (
                        <Box key={f.key} onClick={() => setPkgDlg(d => ({ ...d, closed_keys: on ? [...(d.closed_keys || []), f.key] : (d.closed_keys || []).filter(k => k !== f.key) }))}
                          sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: .5, p: .8, borderRadius: 2,
                                bgcolor: on ? 'rgba(0,229,160,.10)' : C.card2, border: `1px solid ${on ? C.neon : 'transparent'}` }}>
                          <Checkbox size="small" checked={on} sx={{ p: .3, color: C.dim, '&.Mui-checked': { color: C.neon } }} />
                          <Typography sx={{ fontSize: 13, color: on ? C.txt : '#6e8195' }}>{f.title}</Typography>
                        </Box>
                      );
                    })}
                  </Box>
                )}
              </>);
            })()}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPkgDlg(null)} sx={btnText(C.dim)}>Скасувати</Button>
            <Button variant="contained" sx={btnMain} onClick={savePkg}
              disabled={!String(pkgDlg.name || '').trim() || pkgDlg.price_month === '' || !(Number(pkgDlg.daily_limit) >= 1)}>
              {pkgDlg.id ? 'Зберегти' : 'Створити пакет'}
            </Button>
          </DialogActions>
        </>)}
      </Dialog>

      {/* ── [D-620] запуск співпраці ── */}
      <Dialog open={!!coopDlg} onClose={() => setCoopDlg(null)} maxWidth="sm" fullWidth PaperProps={dlgPaper}>
        {coopDlg && (<>
          <DialogTitle>🚀 Запуск співпраці з «{p.name}»</DialogTitle>
          <DialogContent>
            <Typography sx={{ fontSize: 13, color: C.txt, mb: 1.5, lineHeight: 1.6 }}>
              З цієї миті оплати учнів і витрати рахуються по-справжньому; місяць закривається після 1-го числа, підсумок приходить у гілку «Дохід».
              Усе, що було раніше, — тест і не рахується. Частка: вам <b>{p.owner_pct == null ? '—' : p.owner_pct + '%'}</b>, партнеру <b>{p.owner_pct == null ? '—' : num(100 - p.owner_pct) + '%'}</b>.
              Витрати місяця запуску рахуються, якщо їхній «початок» — цей місяць або раніше.
            </Typography>
            <TextField size="small" type="datetime-local" label="Інший початок (за Києвом) — необовʼязково" value={coopDlg.start}
              onChange={e => setCoopDlg(d => ({ ...d, start: e.target.value }))} InputLabelProps={{ ...lblProps, shrink: true }} inputProps={inProps} sx={{ width: 320, ...fieldSx }} />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCoopDlg(null)} sx={btnText(C.dim)}>Скасувати</Button>
            <Button variant="contained" sx={btnMain} disabled={p.owner_pct == null} onClick={saveCoop}>
              {coopDlg.start ? '🚀 Запустити з ' + coopDlg.start.replace('T', ' ') : '🚀 Запустити з цієї хвилини'}
            </Button>
          </DialogActions>
        </>)}
      </Dialog>

      {/* ── [D-618] поповнення ШІ ── */}
      <Dialog open={!!topDlg} onClose={() => setTopDlg(null)} maxWidth="xs" fullWidth PaperProps={dlgPaper}>
        {topDlg && (<>
          <DialogTitle>{topDlg.id ? 'Виправити поповнення ШІ' : 'Поповнення рахунку ШІ'}</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', gap: 1.2, flexWrap: 'wrap', mt: 1 }}>
              <TextField size="small" type="number" label="Сума, €" value={topDlg.amount} onChange={e => setTopDlg(d => ({ ...d, amount: e.target.value }))}
                InputLabelProps={lblProps} inputProps={{ min: 0, step: '0.01', style: { color: C.txt } }} sx={{ width: 140, ...fieldSx }} />
              <TextField size="small" type="month" label="Місяць" value={topDlg.ym} onChange={e => setTopDlg(d => ({ ...d, ym: e.target.value }))}
                InputLabelProps={{ ...lblProps, shrink: true }} inputProps={inProps} sx={{ width: 170, ...fieldSx }} />
              <TextField size="small" label="Примітка (необовʼязково)" value={topDlg.note} onChange={e => setTopDlg(d => ({ ...d, note: e.target.value }))}
                InputLabelProps={lblProps} inputProps={inProps} sx={{ width: '100%', ...fieldSx }} />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setTopDlg(null)} sx={btnText(C.dim)}>Скасувати</Button>
            <Button variant="contained" sx={btnMain} disabled={!(Number(String(topDlg.amount).replace(',', '.')) > 0) || !topDlg.ym} onClick={saveTop}>
              {topDlg.id ? 'Зберегти' : 'Додати'}
            </Button>
          </DialogActions>
        </>)}
      </Dialog>

      {/* ── форма витрати ── */}
      <Dialog open={!!costDlg} onClose={() => setCostDlg(null)} maxWidth="sm" fullWidth PaperProps={dlgPaper}>
        {costDlg && (<>
          <DialogTitle>{costDlg.id ? 'Змінити витрату' : 'Нова витрата на бота'}</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', gap: 1.2, flexWrap: 'wrap', mt: 1 }}>
              <TextField size="small" label="Назва" placeholder="Сервер, ШІ, реклама…" value={costDlg.title} onChange={e => setCostDlg(d => ({ ...d, title: e.target.value }))}
                InputLabelProps={lblProps} inputProps={inProps} sx={{ width: '100%', ...fieldSx }} />
              <TextField size="small" select label="Тип" value={costDlg.kind} onChange={e => setCostDlg(d => ({ ...d, kind: e.target.value }))}
                InputLabelProps={lblProps} SelectProps={{ sx: { color: C.txt } }} sx={{ width: 220, ...fieldSx }}>
                <MenuItem value="monthly">🔵 щомісячна</MenuItem><MenuItem value="once">🟠 разова (вкладено)</MenuItem>
              </TextField>
              <TextField size="small" type="number" label="Сума" value={costDlg.amount} onChange={e => setCostDlg(d => ({ ...d, amount: e.target.value }))}
                InputLabelProps={lblProps} inputProps={{ min: 0, style: { color: C.txt } }} sx={{ width: 130, ...fieldSx }} />
              <TextField size="small" select label="Валюта" value={costDlg.ccy} onChange={e => setCostDlg(d => ({ ...d, ccy: e.target.value }))}
                InputLabelProps={lblProps} SelectProps={{ sx: { color: C.txt } }} sx={{ width: 90, ...fieldSx }}>
                <MenuItem value="UAH">₴</MenuItem><MenuItem value="EUR">€</MenuItem>
              </TextField>
              <TextField size="small" select label="Хто заплатив" value={costDlg.paid_by === 'partner' ? 'partner' : 'owner'}
                onChange={e => setCostDlg(d => ({ ...d, paid_by: e.target.value }))}
                InputLabelProps={lblProps} SelectProps={{ sx: { color: C.txt }, 'data-cost-payer': 1 }} sx={{ width: 220, ...fieldSx }}>   {/* [D-558] */}
                <MenuItem value="owner">👤 я (повернеться мені)</MenuItem><MenuItem value="partner">🤝 партнер (повернеться йому)</MenuItem>
              </TextField>
              <TextField size="small" type="month" label={costDlg.kind === 'once' ? 'Місяць' : 'З місяця'} value={costDlg.start_month || ''}
                onChange={e => setCostDlg(d => ({ ...d, start_month: e.target.value }))} InputLabelProps={{ ...lblProps, shrink: true }} inputProps={inProps} sx={{ width: 190, ...fieldSx }} />
              {costDlg.kind === 'monthly' && (
                <TextField size="small" type="month" label="До місяця (необов'язково)" value={costDlg.end_month || ''}
                  onChange={e => setCostDlg(d => ({ ...d, end_month: e.target.value }))} InputLabelProps={{ ...lblProps, shrink: true }} inputProps={inProps} sx={{ width: 220, ...fieldSx }} />
              )}
            </Box>
            <Box sx={{ mt: 2, p: 1.5, borderRadius: 2, border: `1px dashed ${C.line}` }}>
              <Typography sx={{ color: C.neonTxt, fontWeight: 700, fontSize: 13 }}>🧾 Скрін чека {costDlg.has_receipt ? '(уже є — новий замінить)' : ''}</Typography>
              <Button component="label" size="small" sx={{ ...btnText(C.blue), mt: .5 }}>
                📎 {costDlg.file ? costDlg.file.name : 'Обрати фото або PDF'}
                <input type="file" hidden accept="image/*,application/pdf" onChange={e => setCostDlg(d => ({ ...d, file: (e.target.files && e.target.files[0]) || null }))} />
              </Button>
              <Hint>До 5 МБ. Чек бачите ви й партнер у кабінеті.</Hint>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCostDlg(null)} sx={btnText(C.dim)}>Скасувати</Button>
            <Button variant="contained" sx={btnMain} onClick={saveCost}
              disabled={!String(costDlg.title || '').trim() || costDlg.amount === '' || !(Number(costDlg.amount) >= 0)}>
              {costDlg.id ? 'Зберегти' : 'Додати витрату'}
            </Button>
          </DialogActions>
        </>)}
      </Dialog>

      {/* ── форма переказу ── */}
      <Dialog open={!!setDlg} onClose={() => setSetDlg(null)} maxWidth="xs" fullWidth PaperProps={dlgPaper}>
        {setDlg && (<>
          <DialogTitle>{setDlg.id ? 'Змінити ваш переказ партнеру' : '⬅️ Я переказав партнеру'}</DialogTitle>
          <DialogContent>
            <Hint>Партнер отримає повідомлення й підтвердить отримання в кабінеті — до того переказ у баланс не йде.</Hint>
            <Box sx={{ display: 'flex', gap: 1.2, flexWrap: 'wrap', mt: 1 }}>
              <TextField size="small" type="number" label="Сума" value={setDlg.amount} onChange={e => setSetDlg(d => ({ ...d, amount: e.target.value }))}
                InputLabelProps={lblProps} inputProps={{ min: 0, style: { color: C.txt } }} sx={{ width: 150, ...fieldSx }} />
              <TextField size="small" select label="Валюта" value={setDlg.ccy} onChange={e => setSetDlg(d => ({ ...d, ccy: e.target.value }))}
                InputLabelProps={lblProps} SelectProps={{ sx: { color: C.txt } }} sx={{ width: 90, ...fieldSx }}>
                <MenuItem value="UAH">₴</MenuItem><MenuItem value="EUR">€</MenuItem>
              </TextField>
              <TextField size="small" type="date" label="Дата переказу" value={setDlg.paid_on || ''} onChange={e => setSetDlg(d => ({ ...d, paid_on: e.target.value }))}
                InputLabelProps={{ ...lblProps, shrink: true }} inputProps={inProps} sx={{ width: 170, ...fieldSx }} />
              <TextField size="small" label="Примітка" value={setDlg.note || ''} onChange={e => setSetDlg(d => ({ ...d, note: e.target.value }))}
                InputLabelProps={lblProps} inputProps={inProps} sx={{ width: '100%', ...fieldSx }} />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setSetDlg(null)} sx={btnText(C.dim)}>Скасувати</Button>
            <Button variant="contained" sx={btnMain} onClick={saveSettle} disabled={!(Number(setDlg.amount) > 0)}>Зберегти</Button>
          </DialogActions>
        </>)}
      </Dialog>
    </>
  );
}
